import type React from 'react'
import { useState, useEffect, useCallback } from 'react'
import {
  Globe, Download, ArrowRight, Loader2, RefreshCw, Shield, Database,
  Plug, Server, Eye, EyeOff,
  FileWarning, Trash2, Flag, Send, ChevronDown, ChevronUp, Pencil,
  FileText, Palette, Activity, Store, KeyRound, Cable, Camera,
  Layout, Package, Wrench, MousePointerClick, MonitorSmartphone, AlertTriangle,
  CheckCircle2, XCircle, HelpCircle, Sparkles, Search, ChevronRight,
} from 'lucide-react'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import { getToken } from '@/lib/auth'
import { BrowserPairingControl } from './BrowserPairingControl'
import { useAppStore } from '@/store'

const API_BASE = '/api/dashboard/wordpress'
const PLUGIN_API_BASE = '/api/dashboard/wordpress-plugin'

function apiHeaders() {
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken() || ''}` }
}

interface WpSiteMeta {
  site_id: string
  name: string
  url: string
  connected_at: string
  status: string
  primary: boolean
}

interface SiteHealth {
  wordpress_version?: string
  php_version?: string
  memory_limit?: string
  max_execution_time?: number
  ssl?: boolean
  connector_version?: string
  site_url?: string
  site_title?: string
  multisite?: boolean
}

interface Diagnostics {
  wordpress_version?: string
  php_version?: string
  memory_limit?: string
  max_upload_size?: string
  file_permissions?: Record<string, string>
  ssl_status?: boolean
  debug_mode?: boolean
  cloudflare?: { detected: boolean; ray?: string }
}

interface DbHealth {
  db_version?: string
  table_count?: number
  total_size_mb?: number
  tables?: Array<{ name: string; rows: number; size_mb: number }>
  status?: string
}

interface WpPlugin {
  name: string
  slug?: string
  version: string
  status: string
  update_available?: string
  author?: string
  description?: string
  plugin_file?: string
  file?: string
  icon?: string
  icons?: Record<string, string>
}

interface ErrorLogEntry {
  line: string
  level?: string
}

function StatusDot({ ok }: { ok: boolean }) {
  return <span className={cn('inline-block w-2 h-2 rounded-full', ok ? 'bg-success' : 'bg-danger')} />
}

function PluginStatusDot({ status }: { status: string }) {
  if (status === 'active') return <span className="inline-block w-2 h-2 rounded-full bg-success" title="Active" />
  if (status === 'disabled') return <span className="inline-block w-2 h-2 rounded-full bg-warning" title="Disabled" />
  return <span className="inline-block w-2 h-2 rounded-full bg-danger" title="Inactive" />
}

type WpOrgPluginInfo = {
  name?: string
  slug?: string
  icons?: Record<string, string>
}

const pluginLogoLookupCache = new Map<string, Promise<string[]>>()

function uniqueLogoUrls(urls: Array<string | undefined>) {
  return [...new Set(urls.filter((url): url is string => Boolean(url)))]
}

function pluginCdnSources(slug: string) {
  if (!slug) return []
  const base = `https://ps.w.org/${encodeURIComponent(slug)}/assets`
  return [
    `${base}/icon-256x256.png`,
    `${base}/icon-128x128.png`,
    `${base}/icon-256x256.jpg`,
    `${base}/icon-128x128.jpg`,
    `${base}/icon.svg`,
  ]
}

function pluginInfoIcons(info?: WpOrgPluginInfo) {
  if (!info?.icons) return []
  return uniqueLogoUrls([
    info.icons['2x'],
    info.icons['1x'],
    info.icons.svg,
    info.icons.default,
    ...Object.values(info.icons),
  ])
}

function normalisePluginName(name: string) {
  return name
    .replace(/<[^>]*>/g, '')
    .replace(/&amp;/gi, '&')
    .replace(/[^a-z0-9]+/gi, ' ')
    .trim()
    .toLowerCase()
}

function pluginInitials(name: string) {
  const words = normalisePluginName(name).split(' ').filter(Boolean)
  return (words.length > 1 ? `${words[0][0]}${words[1][0]}` : words[0]?.slice(0, 2) || 'WP').toUpperCase()
}

async function fetchWpOrgPluginLogo(plugin: WpPlugin, slug: string) {
  const cacheKey = `${slug}|${normalisePluginName(plugin.name)}`
  const cached = pluginLogoLookupCache.get(cacheKey)
  if (cached) return cached

  const lookup = (async () => {
    const commonFields: Record<string, string> = {
      'request[fields][icons]': '1',
      'request[fields][sections]': '0',
      'request[fields][description]': '0',
      'request[fields][short_description]': '0',
      'request[fields][versions]': '0',
      'request[fields][banners]': '0',
      'request[fields][screenshots]': '0',
      'request[fields][reviews]': '0',
      'request[fields][contributors]': '0',
    }

    if (slug) {
      try {
        const url = new URL('https://api.wordpress.org/plugins/info/1.2/')
        url.search = new URLSearchParams({ action: 'plugin_information', 'request[slug]': slug, ...commonFields }).toString()
        const response = await fetch(url)
        if (response.ok) {
          const info = await response.json() as WpOrgPluginInfo
          const urls = pluginInfoIcons(info)
          if (urls.length) return urls
        }
      } catch { /* Continue to an exact name search. */ }
    }

    try {
      const url = new URL('https://api.wordpress.org/plugins/info/1.2/')
      url.search = new URLSearchParams({
        action: 'query_plugins',
        'request[search]': plugin.name,
        'request[per_page]': '10',
        ...commonFields,
      }).toString()
      const response = await fetch(url)
      if (!response.ok) return []
      const result = await response.json() as { plugins?: WpOrgPluginInfo[] }
      const targetName = normalisePluginName(plugin.name)
      const exact = result.plugins?.find((item) => normalisePluginName(item.name || '') === targetName)
      if (!exact) return []
      return uniqueLogoUrls([...pluginInfoIcons(exact), ...pluginCdnSources(exact.slug || '')])
    } catch {
      return []
    }
  })()

  pluginLogoLookupCache.set(cacheKey, lookup)
  return lookup
}

function PluginLogo({ plugin }: { plugin: WpPlugin }) {
  const pluginPath = plugin.slug || plugin.file || plugin.plugin_file || ''
  const slug = pluginPath.split('/')[0].replace(/\.php$/i, '').trim().toLowerCase()
  const nativeSources = uniqueLogoUrls([
    plugin.icon,
    plugin.icons?.['2x'],
    plugin.icons?.['1x'],
    plugin.icons?.svg,
    plugin.icons?.default,
    ...(plugin.icons ? Object.values(plugin.icons) : []),
  ])
  const initialSources = uniqueLogoUrls([...nativeSources, ...pluginCdnSources(slug)])
  const [sources, setSources] = useState(initialSources)
  const [sourceIndex, setSourceIndex] = useState(0)
  const [lookupAttempted, setLookupAttempted] = useState(false)

  useEffect(() => {
    setSources(initialSources)
    setSourceIndex(0)
    setLookupAttempted(false)
  }, [plugin.name, plugin.icon, plugin.file, plugin.plugin_file, plugin.slug])

  useEffect(() => {
    if (sources[sourceIndex] || lookupAttempted) return
    let active = true
    setLookupAttempted(true)
    void fetchWpOrgPluginLogo(plugin, slug).then((resolvedSources) => {
      if (!active || !resolvedSources.length) return
      setSources(resolvedSources)
      setSourceIndex(0)
    })
    return () => { active = false }
  }, [lookupAttempted, plugin, slug, sourceIndex, sources])

  if (!sources[sourceIndex]) {
    return (
      <span
        className="ops-wp-plugin-icon ops-wp-plugin-initials"
        title="No published plugin artwork available"
        aria-label={`${plugin.name} artwork unavailable`}
      >
        {pluginInitials(plugin.name)}
      </span>
    )
  }

  return (
    <span className="ops-wp-plugin-icon ops-wp-plugin-logo">
      <img
        src={sources[sourceIndex]}
        alt=""
        loading="lazy"
        referrerPolicy="no-referrer"
        onError={() => setSourceIndex((index) => index + 1)}
      />
    </span>
  )
}

// ─── Disconnected Landing ───────────────────────────────
// ─── Capability grouping: user-facing categories, not raw tool names ───
// Updated 2026-08-22 to reflect the Discovery Engine + security-audit
// session -- the previous 11 groups predated all of this and had no
// category for any of it. Tool count below is NOT sourced from
// useOPS by BakersGuildCapabilities: that pipeline's underlying
// architecture/tools-snapshot.json was confirmed stale since 2026-08-01
// (AD-034's auto-discovery mechanism not firing despite multiple
// ops-bridge-core deploys since), and simply doesn't contain any
// wp_connection_* tools at all, not just an out-of-date count. Using the
// directly-verified live count (WP_CONNECTION_TOOL_COUNT below) instead
// of a broken pipeline's number, until that pipeline is separately fixed.
const WP_CONNECTION_TOOL_COUNT = 229

type WordPressCapabilityGroup = {
  label: string
  blurb: string
  icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>
  count: number
  accent: string
}

const WP_CAPABILITY_GROUPS: WordPressCapabilityGroup[] = [
  {
    label: 'Content & Publishing',
    blurb: 'Create, search, revise, preview, publish, categorise, and manage posts, pages, custom post types, metadata, and revisions.',
    icon: FileText,
    count: 21,
    accent: '#2684ff',
  },
  {
    label: 'Media & AI Images',
    blurb: 'Browse the media library, generate new imagery, and attach generated assets to real WordPress content.',
    icon: Camera,
    count: 3,
    accent: '#6d5dfc',
  },
  {
    label: 'Diagnostics, Discovery & Security',
    blurb: 'Inspect health, routes, logs, cron, performance, plugin behaviour, AJAX safety, code risks, dependencies, theme assets, and verified capability evidence.',
    icon: Activity,
    count: 49,
    accent: '#00a7c7',
  },
  {
    label: 'WooCommerce',
    blurb: 'Review store health, orders, stock, variations, and webhook status, then update real order data through the official API.',
    icon: Store,
    count: 7,
    accent: '#7c5cff',
  },
  {
    label: 'WCFM Marketplace',
    blurb: 'Operate vendors, products, orders, coupons, enquiries, reviews, shipping, notifications, bookings, support, profiles, and marketplace reporting.',
    icon: Globe,
    count: 56,
    accent: '#00a97f',
  },
  {
    label: 'Users & Permissions',
    blurb: 'Inspect users, roles, metadata, and the exact capabilities WordPress has granted before changing an account.',
    icon: KeyRound,
    count: 4,
    accent: '#f59e0b',
  },
  {
    label: 'Browser Automation',
    blurb: 'Open a persistent browser, navigate, click, type, scroll, assert page state, capture screenshots, and run ordered website tests.',
    icon: MousePointerClick,
    count: 10,
    accent: '#0ea5e9',
  },
  {
    label: 'Flatsome & UX Builder',
    blurb: 'Read the site design system, manage UX pages and sections, reuse shortcodes, and update Flatsome settings with the real builder structure.',
    icon: Layout,
    count: 7,
    accent: '#ec4899',
  },
  {
    label: 'SeedProd Design Studio',
    blurb: 'Create and manage landing pages, templates, blocks, sections, display conditions, site modes, subscribers, and SeedProd theme settings.',
    icon: Palette,
    count: 21,
    accent: '#8b5cf6',
  },
  {
    label: 'Plugins & Themes',
    blurb: 'List, install, check, update, bulk-upgrade, and uninstall plugins or themes while retaining the platform’s existing safety controls.',
    icon: Package,
    count: 10,
    accent: '#3b82f6',
  },
  {
    label: 'Database & Cleanup',
    blurb: 'Query site data, inspect size and autoload pressure, remove orphaned data, clean transients, and use reversible table removal before permanent deletion.',
    icon: Database,
    count: 18,
    accent: '#14b8a6',
  },
  {
    label: 'Site Settings',
    blurb: 'Read and update WordPress options, inspect scheduled jobs, clear caches, and rebuild rewrite rules.',
    icon: Wrench,
    count: 6,
    accent: '#64748b',
  },
  {
    label: 'Custom Code',
    blurb: 'Create, inspect, enable, disable, and remove PHP snippets—including targeted temporary mitigations for vulnerable plugin behaviour while an official update is pending.',
    icon: Sparkles,
    count: 5,
    accent: '#f97316',
  },
  {
    label: 'Connections & Licensing',
    blurb: 'Connect or disconnect sites, manage credentials, and administer the licences that control authorised WordPress connections.',
    icon: Cable,
    count: 11,
    accent: '#0284c7',
  },
  {
    label: 'Advanced API Access',
    blurb: 'Use the raw WordPress REST escape hatch for expert operations that are not yet represented by a dedicated capability.',
    icon: Shield,
    count: 1,
    accent: '#ef4444',
  },
]

const WP_ACCENT = '#21759b'
const WP_ACCENT_DARK = '#1a5f7e'

const EXAMPLE_PROMPTS = [
  {
    label: 'Marketplace',
    text: 'Show today’s WCFM orders, low-stock products, vendor notifications, and anything that needs attention.',
  },
  {
    label: 'Design',
    text: 'Create a SeedProd landing page from a template, update the headline and sections, then show me a preview.',
  },
  {
    label: 'Discovery',
    text: 'Inspect this plugin’s REST routes, AJAX actions, settings, permissions, post types, and embedded data structures.',
  },
  {
    label: 'Security',
    text: 'Check this plugin for risky PHP functions and unsafe AJAX handlers. If a safe hook exists, prepare a temporary mitigation snippet for review until the official update arrives.',
  },
  {
    label: 'Theme',
    text: 'Map my Flatsome child-theme hooks, assets, customizer settings, and template overrides before I change anything.',
  },
  {
    label: 'Browser test',
    text: 'Open the live checkout, run the agreed test steps, capture the result, and tell me exactly where anything fails.',
  },
  {
    label: 'Maintenance',
    text: 'Check plugin and theme updates, back up the connector first, then report the health checks needed after upgrading.',
  },
  {
    label: 'Content',
    text: 'Publish the draft post “Summer Collection Launch”, attach the featured image, and verify the live page.',
  },
]

function TerminalPrompt({ label, text }: { label: string; text: string }) {
  return (
    <div className="ops-wp-action-prompt">
      <span className="ops-wp-action-arrow"><ArrowRight className="h-3.5 w-3.5" /></span>
      <div className="min-w-0">
        <span className="ops-wp-action-label">{label}</span>
        <p>{text}</p>
      </div>
    </div>
  )
}

function WordPressCapabilitiesShowcase({ showConnectCta, onConnect }: { showConnectCta: boolean; onConnect?: () => void }) {
  // Directly verified against the VPS worker schema and TOOL_CATEGORIES map.
  // The generated architecture snapshot is still stale and must not be used
  // for this customer-facing count.
  const capCount = WP_CONNECTION_TOOL_COUNT

  return (
    <div className="bg-white dark:bg-[#07090f] overflow-hidden">

      {/* ── 1. Hero — OPS by BakersGuild ──────────────────── */}
      <div className="relative overflow-hidden bg-[#03152d] text-white">
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(circle at 78% 30%, rgba(32,142,255,.32), transparent 34%), radial-gradient(circle at 45% 100%, rgba(0,103,255,.14), transparent 40%)' }} />
        <div className="relative flex flex-col lg:flex-row items-center gap-10 px-5 sm:px-9 pt-12 pb-11 lg:px-14 lg:pt-16 lg:pb-14">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 rounded-xl border border-sky-400/50 bg-sky-400/10 flex items-center justify-center shadow-[0_0_24px_rgba(56,189,248,.2)]"><Plug className="h-5 w-5 text-sky-300" /></div>
              <div><div className="font-bold text-base leading-none">OPS <span className="font-normal text-white/65">by BakersGuild</span></div><div className="text-[10px] text-sky-300/80 mt-1">Smarter WordPress, together</div></div>
            </div>
            <p className="text-[11px] font-bold uppercase tracking-[.18em] text-sky-400 mb-3">OPS Connector</p>
            <h1 className="text-[2.55rem] sm:text-[3.15rem] lg:text-[3.5rem] font-extrabold leading-[1.02] tracking-tight mb-5 max-w-2xl">The WordPress Connection <span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-300 to-blue-500">You’ve Been Waiting For.</span></h1>
            <p className="text-base text-slate-300 leading-relaxed mb-7 max-w-xl">Connect your WordPress site to the power of OPS by BakersGuild. Analyse, troubleshoot, and improve your website with AI — faster, safer, and smarter.</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-7 max-w-xl text-xs text-slate-300"><span className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-sky-400" />Works with WordPress</span><span className="flex items-center gap-2"><Activity className="h-4 w-4 text-sky-400" />Save time, work smarter</span><span className="flex items-center gap-2"><Shield className="h-4 w-4 text-sky-400" />Built for developers & owners</span></div>
            <div className="flex flex-wrap gap-3">
              <a href="#wp-install" onClick={(e) => { e.preventDefault(); document.getElementById('wp-install')?.scrollIntoView({ behavior: 'smooth' }) }} className="inline-flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-sky-500 hover:brightness-110 shadow-[0_8px_28px_rgba(14,165,233,.28)] cursor-pointer"><Download className="h-4 w-4" />Download WordPress Plugin</a>
              <a href="#wp-extension-install" onClick={(e) => { e.preventDefault(); document.getElementById('wp-extension-install')?.scrollIntoView({ behavior: 'smooth' }) }} className="inline-flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-semibold text-white border border-sky-400/50 bg-white/[.04] hover:bg-white/[.08] cursor-pointer"><MonitorSmartphone className="h-4 w-4 text-sky-300" />Download Browser Extension</a>
              {showConnectCta && <button onClick={onConnect} className="inline-flex items-center gap-2 px-4 py-3 text-sm text-slate-300 hover:text-white cursor-pointer">Already installed <ArrowRight className="h-4 w-4" /></button>}
            </div>
          </div>
          <div className="flex-1 min-w-0 w-full max-w-[590px] lg:rotate-[2deg]"><div className="relative rounded-[1.6rem] p-2 bg-gradient-to-br from-sky-400/40 via-blue-500/10 to-transparent shadow-[0_25px_80px_rgba(0,113,255,.35)]"><div className="absolute -inset-10 bg-blue-500/20 blur-3xl rounded-full pointer-events-none" /><img src="https://ops-admin-gateway.ops-a58.workers.dev/dashboard/images/IMG-20260822-262" alt="OPS Connector WordPress interface" className="relative w-full rounded-[1.25rem] border border-sky-300/20" /></div></div>
        </div>
        <div className="relative border-t border-white/[.08] grid grid-cols-2 lg:grid-cols-4">
          {[{ icon: AlertTriangle, title: 'Detect Issues Early', text: 'Find conflicts and configuration problems before they cause issues.' },{ icon: Activity, title: 'Improve Performance', text: 'Get AI-driven insight to make your site faster and more efficient.' },{ icon: Shield, title: 'Enhance Security', text: 'Review risky patterns and strengthen your WordPress setup.' },{ icon: Sparkles, title: 'Work Smarter', text: 'Use AI-powered analysis, guidance, and actionable insight.' }].map((item) => { const Icon = item.icon; return <div key={item.title} className="p-6 border-r border-b lg:border-b-0 border-white/[.07] last:border-r-0"><div className="w-10 h-10 rounded-xl bg-blue-500/15 border border-sky-400/20 flex items-center justify-center mb-4"><Icon className="h-5 w-5 text-sky-400" /></div><h3 className="font-semibold text-sm mb-1.5">{item.title}</h3><p className="text-xs text-slate-400 leading-relaxed">{item.text}</p></div> })}
        </div>
      </div>

      {/* ── 2. How it works ─────────────────────────────────── */}
      <div className="px-5 sm:px-9 py-14 lg:px-14 bg-[#f7fbff] text-slate-900">
        <p className="text-[11px] font-bold uppercase tracking-[.18em] text-blue-600 mb-2">How it works</p><h2 className="text-3xl font-extrabold tracking-tight mb-2">Simple. Powerful. Effective.</h2><p className="text-sm text-slate-500 mb-9 max-w-xl">Get started in minutes and unlock the full WordPress capability of OPS Connector.</p>
        <div className="grid md:grid-cols-2 gap-8 items-center"><div className="space-y-6">
          {[{ n: '1', title: 'Install', text: 'Download and activate the OPS Connector WordPress plugin.', icon: Download },{ n: '2', title: 'Connect', text: 'Securely connect your WordPress site to OPS by BakersGuild.', icon: Cable },{ n: '3', title: 'Analyse', text: 'Run AI-powered checks and get clear, evidence-backed insight.', icon: Activity },{ n: '4', title: 'Improve', text: 'Act on recommendations and make your site better, faster, and safer.', icon: Sparkles }].map((s) => { const Icon = s.icon; return <div key={s.n} className="flex gap-4"><div className="w-9 h-9 shrink-0 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-bold shadow-md">{s.n}</div><div><div className="flex items-center gap-2"><Icon className="h-4 w-4 text-blue-600" /><h3 className="font-bold">{s.title}</h3></div><p className="text-sm text-slate-500 mt-1">{s.text}</p></div></div> })}
          <a href="#wp-install" onClick={(e) => { e.preventDefault(); document.getElementById('wp-install')?.scrollIntoView({ behavior: 'smooth' }) }} className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-500 cursor-pointer">Get Started Now <ArrowRight className="h-4 w-4" /></a>
        </div><div className="relative rounded-2xl overflow-hidden border border-blue-100 shadow-[0_20px_60px_rgba(37,99,235,.15)]"><img src="https://ops-admin-gateway.ops-a58.workers.dev/dashboard/images/IMG-20260822-262" alt="OPS Connector site analysis dashboard" className="w-full" /></div></div>
      </div>
      <div className="relative overflow-hidden px-5 sm:px-9 py-14 lg:px-14 bg-[#041a36] text-white border-t border-white/[.06]">
        <div className="absolute right-[-8rem] top-[-8rem] w-[30rem] h-[30rem] rounded-full bg-blue-500/15 blur-3xl pointer-events-none" /><p className="relative text-[11px] font-bold uppercase tracking-[.18em] text-sky-400 mb-2">Built for everyone</p><h2 className="relative text-3xl font-extrabold tracking-tight mb-2">For Developers and Site Owners</h2><p className="relative text-sm text-slate-400 max-w-2xl mb-8">Whether you’re building complex WordPress projects or simply want a better WordPress experience, OPS Connector gives your AI the tools to work faster and smarter.</p>
        <div className="relative grid md:grid-cols-2 gap-4 max-w-3xl"><div className="rounded-2xl p-6 bg-white/[.035] border border-sky-400/15"><h3 className="font-bold flex items-center gap-2 mb-4"><FileText className="h-5 w-5 text-sky-400" />For Developers</h3>{['Check plugin compatibility','Analyse code and configuration','Debug issues faster','Discover plugin capabilities with evidence'].map((t) => <p key={t} className="text-sm text-slate-300 flex gap-2 mb-2"><CheckCircle2 className="h-4 w-4 text-sky-400 shrink-0 mt-0.5" />{t}</p>)}</div><div className="rounded-2xl p-6 bg-white/[.035] border border-sky-400/15"><h3 className="font-bold flex items-center gap-2 mb-4"><Globe className="h-5 w-5 text-sky-400" />For Site Owners</h3>{['Keep your site healthy and secure','Get easy-to-understand insights','Improve performance without guesswork','Manage your site with confidence'].map((t) => <p key={t} className="text-sm text-slate-300 flex gap-2 mb-2"><CheckCircle2 className="h-4 w-4 text-sky-400 shrink-0 mt-0.5" />{t}</p>)}</div></div>
      </div>

      {/* ── 3b. Discovery Engine + Security Audit spotlight ─────────── */}
      <div className="px-4 sm:px-8 py-12 lg:px-12 bg-slate-50 dark:bg-white/[0.015] border-t border-slate-100 dark:border-white/[0.05]">
        <div className="mb-10">
          <p className="text-[11px] font-bold uppercase tracking-widest mb-2" style={{ color: WP_ACCENT }}>New</p>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white">Discover, then verify — before anything's trusted</h2>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 max-w-2xl">
            Install any plugin and the AI investigates it properly first — its real REST routes, AJAX actions, settings, and capabilities — with genuine evidence, then checks it for known-risk patterns before anything gets exposed as a tool.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 gap-6">
          <div className="bg-white dark:bg-white/[0.04] rounded-2xl p-6 border border-slate-100 dark:border-white/[0.06] overflow-hidden">
            <img src="https://ops-admin-gateway.ops-a58.workers.dev/dashboard/images/IMG-20260822-895"
              alt="Discovery Engine illustration" className="w-full rounded-xl mb-5" />
            <h3 className="text-base font-semibold text-slate-900 dark:text-white mb-2">Discovery Engine</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed">
              Every discovered route, action, and setting is evidence-backed and tested against the live site — not assumed from a plugin's name or reputation.
            </p>
          </div>
          <div className="bg-white dark:bg-white/[0.04] rounded-2xl p-6 border border-slate-100 dark:border-white/[0.06] overflow-hidden">
            <img src="https://ops-admin-gateway.ops-a58.workers.dev/dashboard/images/IMG-20260822-744"
              alt="Security Audit illustration" className="w-full rounded-xl mb-5" />
            <h3 className="text-base font-semibold text-slate-900 dark:text-white mb-2">Security Audit</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed">
              Known-risk function scanning, cross-plugin conflict detection, and AJAX handler safety checks — real findings for you to review, never a false pass/fail.
            </p>
          </div>
        </div>
      </div>

      {/* ── 4. Capability groups — verified against the live worker ── */}
      <section className="ops-wp-capabilities-section">
        <div className="ops-wp-capabilities-heading">
          <div>
            <p className="ops-wp-section-kicker">Connected capability layer</p>
            <h2>Everything OPS can do with your WordPress site</h2>
            <p>
              Real, permission-scoped capabilities verified against the current worker—not a generic feature list.
            </p>
          </div>
          <div className="ops-wp-capability-total" aria-label={capCount + ' capabilities across ' + WP_CAPABILITY_GROUPS.length + ' categories'}>
            <strong>{capCount}</strong>
            <span>capabilities</span>
            <small>{WP_CAPABILITY_GROUPS.length} protected categories</small>
          </div>
        </div>

        <div className="ops-wp-capability-grid">
          {WP_CAPABILITY_GROUPS.map((group) => {
            const Icon = group.icon
            return (
              <article
                key={group.label}
                className="ops-wp-capability-card"
                style={{ '--cap-accent': group.accent } as React.CSSProperties}
              >
                <div className="ops-wp-capability-card-top">
                  <span className="ops-wp-capability-card-icon">
                    <Icon className="h-4 w-4" />
                  </span>
                  <span className="ops-wp-capability-count">{group.count}</span>
                </div>
                <h3>{group.label}</h3>
                <p>{group.blurb}</p>
                <div className="ops-wp-capability-status">
                  <span />
                  Permission controlled
                </div>
              </article>
            )
          })}
        </div>
      </section>

      {/* ── 5. Real-world examples from the verified capability set ── */}
      <section className="ops-wp-action-section">
        <div className="ops-wp-action-heading">
          <div>
            <p className="ops-wp-section-kicker">In action</p>
            <h2>Ask naturally. OPS handles the technical steps.</h2>
            <p>One request can combine WordPress, WooCommerce, WCFM, browser, design, and diagnostic capabilities while preserving their permission gates.</p>
          </div>
          <div className="ops-wp-action-ready">
            <span />
            WordPress connected
          </div>
        </div>

        <div className="ops-wp-action-console">
          <div className="ops-wp-action-console-bar">
            <div className="ops-wp-action-console-dots"><span /><span /><span /></div>
            <div>
              <strong>OPS by BakersGuild</strong>
              <small>Verified WordPress capability examples</small>
            </div>
            <span className="ops-wp-action-console-count">{EXAMPLE_PROMPTS.length} examples</span>
          </div>
          <div className="ops-wp-action-grid">
            {EXAMPLE_PROMPTS.map((prompt) => (
              <TerminalPrompt key={prompt.label} label={prompt.label} text={prompt.text} />
            ))}
          </div>
          <div className="ops-wp-action-console-footer">
            <Sparkles className="h-3.5 w-3.5" />
            <span>Ready for your next instruction</span>
          </div>
        </div>
      </section>

      {/* ── 6. Bottom CTA (pre-connect only) ────────────────── */}
      {showConnectCta && (
        <div className="px-4 sm:px-8 py-14 lg:px-12 text-center"
          style={{ background: `linear-gradient(135deg, ${WP_ACCENT} 0%, ${WP_ACCENT_DARK} 100%)` }}>
          <Shield className="h-10 w-10 mx-auto mb-4" style={{ color: 'rgba(255,255,255,0.5)' }} />
          <h2 className="text-2xl font-bold text-white mb-3">Ready to connect your site?</h2>
          <p className="text-sm mb-8 max-w-sm mx-auto leading-relaxed" style={{ color: 'rgba(255,255,255,0.72)' }}>
            Uses WordPress Application Passwords — no OAuth flow, no third-party auth. Your credentials stay on your own infrastructure.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <a href="#wp-install" onClick={(e) => { e.preventDefault(); document.getElementById('wp-install')?.scrollIntoView({ behavior: 'smooth' }) }}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold transition-colors hover:bg-slate-50 cursor-pointer"
              style={{ background: '#ffffff', color: WP_ACCENT, boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }}>
              <Download className="h-4 w-4" />
              Install Plugin
            </a>
            <button onClick={onConnect}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold text-white border border-white/30 hover:bg-white/10 transition-colors cursor-pointer">
              Already installed — Connect now
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* ── 7–8. Live-source downloads ─────────────────────── */}
      <section className="ops-wp-downloads-section">
        <div id="wp-install" className="scroll-mt-4">
          <DownloadPluginTab />
        </div>
        <div id="wp-extension-install" className="scroll-mt-4">
          <DownloadExtensionTab />
        </div>
      </section>

    </div>
  )
}

function DisconnectedLanding({ onConnect }: { onConnect: () => void }) {
  return <WordPressCapabilitiesShowcase showConnectCta onConnect={onConnect} />
}


// ─── Connection Form ───────────────────────────────────
function ConnectionForm({ onSuccess, onCancel }: { onSuccess: () => void; onCancel: () => void }) {
  const [url, setUrl] = useState('')
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [name, setName] = useState('')
  const [showPw, setShowPw] = useState(false)
  const [testing, setTesting] = useState(false)
  const [error, setError] = useState('')

  const handleConnect = async () => {
    setError('')
    const cleanUrl = url.replace(/\/+$/, '')
    if (!cleanUrl || !username || !password) { setError('All fields are required'); return }
    setTesting(true)
    try {
      const res = await fetch(`${API_BASE}/connect`, {
        method: 'POST',
        headers: apiHeaders(),
        body: JSON.stringify({ url: cleanUrl, username, app_password: password, name: name || undefined }),
      })
      const data = await res.json()
      if (!res.ok) { setError(data.error || 'Connection failed'); setTesting(false); return }
      onSuccess()
    } catch {
      setError('Could not connect. Check your URL, username, and Application Password.')
    } finally { setTesting(false) }
  }

  return (
    <Card className="max-w-lg mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-sm">
          <Globe className="h-4 w-4" />
          Connect WordPress Site
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-3">
          <div>
            <label className="text-xs text-text-muted block mb-1">Site URL</label>
            <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://yoursite.com"
              className="w-full px-3 py-2 text-sm bg-surface-elevated border border-border rounded-lg text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent" />
          </div>
          <div>
            <label className="text-xs text-text-muted block mb-1">WordPress Username</label>
            <input value={username} onChange={(e) => setUsername(e.target.value)} placeholder="admin"
              className="w-full px-3 py-2 text-sm bg-surface-elevated border border-border rounded-lg text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent" />
          </div>
          <div>
            <label className="text-xs text-text-muted block mb-1">Application Password</label>
            <div className="relative">
              <input value={password} onChange={(e) => setPassword(e.target.value)} type={showPw ? 'text' : 'password'} placeholder="xxxx xxxx xxxx xxxx xxxx xxxx"
                className="w-full px-3 py-2 pr-10 text-sm bg-surface-elevated border border-border rounded-lg text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent" />
              <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-2 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-primary cursor-pointer">
                {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <p className="text-[10px] text-text-muted mt-1">Users → Profile → Application Passwords in your WP admin</p>
          </div>
          <div>
            <label className="text-xs text-text-muted block mb-1">Site Name (optional)</label>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="My Blog"
              className="w-full px-3 py-2 text-sm bg-surface-elevated border border-border rounded-lg text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent" />
          </div>
        </div>
        {error && <p className="text-xs text-danger">{error}</p>}
        <div className="flex gap-2">
          <button onClick={handleConnect} disabled={testing}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium bg-accent text-white rounded-lg hover:bg-accent/90 disabled:opacity-50 transition-colors cursor-pointer">
            {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
            {testing ? 'Testing connection…' : 'Connect'}
          </button>
          <button onClick={onCancel} className="px-4 py-2 text-sm text-text-muted hover:text-text-primary transition-colors cursor-pointer">Cancel</button>
        </div>
      </CardContent>
    </Card>
  )
}

// ─── Update Credentials Modal ─────────────────────────
function UpdateCredentialsModal({ site, onDone }: { site: WpSiteMeta; onDone: () => void }) {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [showPw, setShowPw] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const handleUpdate = async () => {
    if (!username || !password) { setError('Both fields required'); return }
    setSaving(true); setError('')
    try {
      const res = await fetch(`${API_BASE}/sites/${site.site_id}/credentials`, {
        method: 'PUT',
        headers: apiHeaders(),
        body: JSON.stringify({ username, app_password: password }),
      })
      const data = await res.json()
      if (!res.ok) { setError(data.error || 'Update failed'); setSaving(false); return }
      onDone()
    } catch {
      setError('Connection test failed with new credentials.')
    } finally { setSaving(false) }
  }

  return (
    <div className="p-4 border border-border rounded-lg bg-surface-elevated space-y-3">
      <p className="text-sm font-medium text-text-primary">Update Credentials — {site.name}</p>
      <div>
        <label className="text-xs text-text-muted block mb-1">Username</label>
        <input value={username} onChange={(e) => setUsername(e.target.value)} placeholder="admin"
          className="w-full px-3 py-2 text-sm bg-surface border border-border rounded-lg text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent" />
      </div>
      <div>
        <label className="text-xs text-text-muted block mb-1">New Application Password</label>
        <div className="relative">
          <input value={password} onChange={(e) => setPassword(e.target.value)} type={showPw ? 'text' : 'password'} placeholder="xxxx xxxx xxxx xxxx"
            className="w-full px-3 py-2 pr-10 text-sm bg-surface border border-border rounded-lg text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent" />
          <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-2 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-primary cursor-pointer">
            {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
      </div>
      {error && <p className="text-xs text-danger">{error}</p>}
      <div className="flex gap-2">
        <button onClick={handleUpdate} disabled={saving}
          className="flex items-center gap-1 px-3 py-1.5 text-xs bg-accent text-white rounded hover:bg-accent/90 disabled:opacity-50 cursor-pointer">
          {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
          {saving ? 'Testing…' : 'Update'}
        </button>
        <button onClick={onDone} className="px-3 py-1.5 text-xs text-text-muted hover:text-text-primary cursor-pointer">Cancel</button>
      </div>
    </div>
  )
}

// ─── Delete Confirmation Modal ──────────────────────────
function DeleteConfirmModal({ site, onDone, onCancel }: { site: WpSiteMeta; onDone: () => void; onCancel: () => void }) {
  const [confirmText, setConfirmText] = useState('')
  const [deleting, setDeleting] = useState(false)

  const handleDelete = async () => {
    setDeleting(true)
    try {
      await fetch(`${API_BASE}/sites/${site.site_id}`, {
        method: 'DELETE',
        headers: apiHeaders(),
        body: JSON.stringify({ confirmation_text: confirmText }),
      })
      onDone()
    } finally { setDeleting(false) }
  }

  return (
    <div className="p-4 border border-danger/30 rounded-lg bg-danger/5 space-y-3">
      <p className="text-sm font-medium text-danger">Delete {site.name}?</p>
      <p className="text-xs text-text-muted">This will permanently remove {site.name} and all stored credentials.</p>
      <div>
        <label className="text-xs text-text-muted block mb-1">Type DELETE to confirm</label>
        <input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="DELETE"
          className="w-full px-3 py-2 text-sm bg-surface border border-border rounded-lg text-text-primary placeholder:text-text-muted focus:outline-none focus:border-danger" />
      </div>
      <div className="flex gap-2">
        <button onClick={handleDelete} disabled={confirmText !== 'DELETE' || deleting}
          className="flex items-center gap-1 px-3 py-1.5 text-xs bg-danger text-white rounded hover:bg-danger/90 disabled:opacity-50 cursor-pointer">
          {deleting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
          Delete Site
        </button>
        <button onClick={onCancel} className="px-3 py-1.5 text-xs text-text-muted hover:text-text-primary cursor-pointer">Cancel</button>
      </div>
    </div>
  )
}

// ─── Site Health Panel ────────────────────────────────────
function SiteHealthCard({ site }: { site: WpSiteMeta }) {
  const [health, setHealth] = useState<SiteHealth | null>(null)
  const [diag, setDiag] = useState<Diagnostics | null>(null)
  const [loading, setLoading] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const globalRefreshVersion = useAppStore((s) => s.globalRefreshVersion)

  const fetchHealth = useCallback(async () => {
    setLoading(true)
    try {
      const res = await fetch(`${API_BASE}/sites/${site.site_id}/health`, { headers: apiHeaders() })
      if (res.ok) setHealth(await res.json())
    } catch { /* ignore */ }
    try {
      const res = await fetch(`${API_BASE}/sites/${site.site_id}/diagnostics`, { headers: apiHeaders() })
      if (res.ok) setDiag(await res.json())
    } catch { /* ignore */ }
    setLoading(false)
  }, [site.site_id])

  useEffect(() => { fetchHealth() }, [fetchHealth, globalRefreshVersion])

  return (
    <Card className="ops-wp-card ops-wp-health-card">
      <CardHeader className="ops-wp-card-header">
        <CardTitle className="flex items-center gap-3 text-base">
          <span className="ops-wp-section-icon ops-wp-section-icon-success"><CheckCircle2 className="h-5 w-5" /></span>
          <span>Site Health</span>
          {loading && <Loader2 className="h-3.5 w-3.5 animate-spin text-text-muted" />}
          <button onClick={fetchHealth} className="ops-wp-refresh ml-auto" aria-label="Refresh site health"><RefreshCw className="h-4 w-4" /></button>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {health ? (
          <div className="space-y-3">
            <div className="ops-wp-health-rows">
              <Row label="WordPress" value={health.wordpress_version || 'Not reported'} />
              <Row label="PHP" value={health.php_version || 'Not reported'} />
              <Row label="Memory Limit" value={health.memory_limit || 'Not reported'} />
              <Row label="SSL" value={health.ssl ? 'Active' : 'Inactive'} icon={health.ssl ? <StatusDot ok /> : <StatusDot ok={false} />} />
              <Row label="Connector" value={health.connector_version || 'Not reported'} />
              {diag?.cloudflare && (
                <Row label="Cloudflare" value={diag.cloudflare.detected ? `Active (${diag.cloudflare.ray || ''})` : 'Not detected'}
                  icon={<Shield className={cn('h-3.5 w-3.5', diag.cloudflare.detected ? 'text-success' : 'text-text-muted')} />} />
              )}
            </div>
            {diag && (
              <>
                <button onClick={() => setExpanded(!expanded)} className="ops-wp-diagnostics-toggle">
                  {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  {expanded ? 'Hide' : 'Show'} diagnostics
                </button>
                {expanded && (
                  <div className="ops-wp-diagnostics space-y-1 text-xs">
                    <Row label="Max Upload" value={diag.max_upload_size || 'Not reported'} small />
                    <Row label="Debug Mode" value={diag.debug_mode ? 'ON' : 'OFF'} small />
                    {diag.file_permissions && Object.entries(diag.file_permissions).map(([k, v]) => (
                      <Row key={k} label={k} value={v} small />
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        ) : !loading ? (
          <p className="text-sm text-text-muted">Unable to fetch site health</p>
        ) : null}
      </CardContent>
    </Card>
  )
}

// ─── Database Panel ────────────────────────────────────────
function DatabaseCard({ site }: { site: WpSiteMeta }) {
  const [db, setDb] = useState<DbHealth | null>(null)
  const [loading, setLoading] = useState(false)
  const globalRefreshVersion = useAppStore((s) => s.globalRefreshVersion)

  const fetchDb = useCallback(async () => {
    setLoading(true)
    try {
      const res = await fetch(`${API_BASE}/sites/${site.site_id}/db-health`, { headers: apiHeaders() })
      if (res.ok) setDb(await res.json())
    } catch { /* ignore */ }
    setLoading(false)
  }, [site.site_id])

  useEffect(() => { fetchDb() }, [fetchDb, globalRefreshVersion])

  const statusColor = db?.status === 'healthy' ? 'text-success' : db?.status === 'warning' ? 'text-warning' : 'text-danger'

  return (
    <Card className="ops-wp-card">
      <CardHeader className="ops-wp-card-header">
        <CardTitle className="flex items-center gap-3 text-base">
          <span className="ops-wp-section-icon"><Database className="h-5 w-5" /></span>
          <span>Database</span>
          {loading && <Loader2 className="h-3.5 w-3.5 animate-spin text-text-muted" />}
          {db?.status && <Badge variant={db.status === 'healthy' ? 'success' : db.status === 'warning' ? 'warning' : 'danger'} className="ml-auto capitalize">{db.status}</Badge>}
          <button onClick={fetchDb} className={cn('ops-wp-refresh', !db?.status && 'ml-auto')} aria-label="Refresh database health"><RefreshCw className="h-4 w-4" /></button>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {db ? (
          <div className="space-y-4">
            <div className="ops-wp-db-grid">
              <div className="ops-wp-stat-cell">
                <div className="text-xl font-bold text-text-primary">{db.table_count ?? '—'}</div>
                <div className="text-xs text-text-muted">Tables</div>
              </div>
              <div className="ops-wp-stat-cell">
                <div className={cn('text-xl font-bold', statusColor)}>{db.total_size_mb?.toFixed(1) ?? '—'}</div>
                <div className="text-xs text-text-muted">Size (MB)</div>
              </div>
              <div className="ops-wp-stat-cell">
                <div className="text-xl font-bold text-text-primary">{db.db_version || '—'}</div>
                <div className="text-xs text-text-muted">DB Version</div>
              </div>
            </div>
            {db.tables && db.tables.length > 0 && (
              <div className="space-y-1">
                <p className="text-xs text-text-muted font-medium">Largest tables</p>
                {db.tables.slice(0, 10).map((t) => (
                  <div key={t.name} className="flex items-center justify-between text-xs py-2 border-b border-border/60 last:border-0">
                    <span className="text-text-primary font-mono truncate max-w-[60%]">{t.name}</span>
                    <span className="text-text-muted">{t.rows} rows · {t.size_mb?.toFixed(2)} MB</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : !loading ? (
          <p className="text-sm text-text-muted">Unable to fetch database health</p>
        ) : null}
      </CardContent>
    </Card>
  )
}

// ─── Plugins Panel ──────────────────────────────────────────
function PluginsCard({ site }: { site: WpSiteMeta }) {
  const [plugins, setPlugins] = useState<WpPlugin[]>([])
  const [loading, setLoading] = useState(false)
  const [flagging, setFlagging] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [pluginFilter, setPluginFilter] = useState<'all' | 'active' | 'updates'>('all')
  const [flagForm, setFlagForm] = useState({ issueType: 'Bug', description: '', severity: 'medium', reproducibility: 'intermittent' })
  const globalRefreshVersion = useAppStore((s) => s.globalRefreshVersion)

  const fetchPlugins = useCallback(async () => {
    setLoading(true)
    try {
      const res = await fetch(`${API_BASE}/sites/${site.site_id}/plugins`, { headers: apiHeaders() })
      if (res.ok) { const d = await res.json(); setPlugins(Array.isArray(d.plugins) ? d.plugins : Array.isArray(d) ? d : []) }
    } catch { /* ignore */ }
    setLoading(false)
  }, [site.site_id])

  useEffect(() => { fetchPlugins() }, [fetchPlugins, globalRefreshVersion])

  const visiblePlugins = plugins.filter((plugin) => {
    const matchesSearch = plugin.name.toLowerCase().includes(search.trim().toLowerCase())
    const matchesFilter = pluginFilter === 'all'
      || (pluginFilter === 'active' && plugin.status === 'active')
      || (pluginFilter === 'updates' && !!plugin.update_available)
    return matchesSearch && matchesFilter
  })

  return (
    <Card className="ops-wp-card">
      <CardHeader className="ops-wp-card-header">
        <CardTitle className="flex items-center gap-3 text-base">
          <span className="ops-wp-section-icon"><Plug className="h-5 w-5" /></span>
          <span>Plugins</span>
          {loading && <Loader2 className="h-3.5 w-3.5 animate-spin text-text-muted" />}
          <Badge variant="outline" className="ml-auto rounded-full px-2.5">{plugins.length}</Badge>
          <button onClick={fetchPlugins} className="ops-wp-refresh" aria-label="Refresh plugins"><RefreshCw className="h-4 w-4" /></button>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="ops-wp-plugin-toolbar">
          <label className="ops-wp-plugin-search">
            <Search className="h-4 w-4" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search plugins" aria-label="Search plugins" />
          </label>
          <div className="ops-wp-plugin-filters" aria-label="Filter plugins">
            {([
              { id: 'all' as const, label: 'All' },
              { id: 'active' as const, label: 'Active' },
              { id: 'updates' as const, label: 'Updates' },
            ]).map((filter) => (
              <button key={filter.id} onClick={() => setPluginFilter(filter.id)}
                className={cn('ops-wp-filter-chip', pluginFilter === filter.id && 'is-active')}>
                {filter.label}
              </button>
            ))}
          </div>
        </div>

        <div className="ops-wp-plugin-list">
          {visiblePlugins.map((plugin) => (
            <div key={plugin.name}>
              <div className="ops-wp-plugin-row">
                <PluginLogo plugin={plugin} />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-text-primary truncate">{plugin.name}</div>
                  <div className="text-[11px] text-text-muted">v{plugin.version}{plugin.author ? ` · ${plugin.author}` : ''}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {plugin.update_available && (
                    <Badge variant="warning" className="text-[10px]">Update {plugin.update_available}</Badge>
                  )}
                  <span className={cn('ops-wp-plugin-status',
                    plugin.status === 'active' ? 'is-active' : plugin.status === 'disabled' ? 'is-disabled' : 'is-inactive')}>
                    <PluginStatusDot status={plugin.status} />
                    {plugin.status}
                  </span>
                  <button onClick={() => setFlagging(flagging === plugin.name ? null : plugin.name)}
                    className="ops-wp-flag" title="Flag issue" aria-label={`Flag an issue with ${plugin.name}`}>
                    <Flag className="h-3.5 w-3.5" />
                  </button>
                  <ChevronRight className="h-4 w-4 text-text-muted/70" />
                </div>
              </div>
              {flagging === plugin.name && (
                <div className="ops-wp-flag-form">
                  <p className="text-xs font-medium text-text-primary">Flag Issue — {plugin.name}</p>
                  <div className="grid grid-cols-2 gap-2">
                    <select value={flagForm.issueType} onChange={(e) => setFlagForm({ ...flagForm, issueType: e.target.value })}
                      className="text-xs px-2 py-1.5 bg-surface border border-border rounded text-text-primary">
                      <option>Security</option><option>Bug</option><option>Compatibility</option><option>Performance</option>
                    </select>
                    <select value={flagForm.severity} onChange={(e) => setFlagForm({ ...flagForm, severity: e.target.value })}
                      className="text-xs px-2 py-1.5 bg-surface border border-border rounded text-text-primary">
                      <option value="critical">Critical</option><option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option>
                    </select>
                  </div>
                  <textarea value={flagForm.description} onChange={(e) => setFlagForm({ ...flagForm, description: e.target.value })}
                    placeholder="Describe the issue…" rows={3}
                    className="w-full text-xs px-2 py-1.5 bg-surface border border-border rounded text-text-primary placeholder:text-text-muted resize-none" />
                  <select value={flagForm.reproducibility} onChange={(e) => setFlagForm({ ...flagForm, reproducibility: e.target.value })}
                    className="w-full text-xs px-2 py-1.5 bg-surface border border-border rounded text-text-primary">
                    <option value="always">Always reproducible</option><option value="intermittent">Intermittent</option><option value="once">Only once</option>
                  </select>
                  <div className="flex gap-2">
                    <button className="flex items-center gap-1 px-3 py-1.5 text-xs bg-accent/10 text-accent rounded hover:bg-accent/20 transition-colors cursor-pointer">
                      <Send className="h-3 w-3" />
                      Send to Author
                    </button>
                    <button onClick={() => setFlagging(null)} className="px-3 py-1.5 text-xs text-text-muted hover:text-text-primary cursor-pointer">Cancel</button>
                  </div>
                </div>
              )}
            </div>
          ))}
          {visiblePlugins.length === 0 && !loading && (
            <p className="text-sm text-text-muted text-center py-6">
              {plugins.length === 0 ? 'No plugins found' : 'No plugins match this view'}
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

// ─── Error Log Panel ──────────────────────────────────────────
function ErrorLogCard({ site }: { site: WpSiteMeta }) {
  const [lines, setLines] = useState<ErrorLogEntry[]>([])
  const [loading, setLoading] = useState(false)
  const [filter, setFilter] = useState<string>('all')
  const globalRefreshVersion = useAppStore((s) => s.globalRefreshVersion)

  const fetchLog = useCallback(async () => {
    setLoading(true)
    try {
      const res = await fetch(`${API_BASE}/sites/${site.site_id}/error-log`, { headers: apiHeaders() })
      if (res.ok) {
        const d = await res.json()
        const raw: string[] = d.lines || d.log || []
        setLines(raw.map((l: string) => ({
          line: l,
          level: /fatal|critical/i.test(l) ? 'critical' : /error/i.test(l) ? 'error' : /warning|deprecated/i.test(l) ? 'warning' : /notice/i.test(l) ? 'notice' : 'info',
        })))
      }
    } catch { /* ignore */ }
    setLoading(false)
  }, [site.site_id])

  useEffect(() => { fetchLog() }, [fetchLog, globalRefreshVersion])

  const filtered = filter === 'all' ? lines : lines.filter((l) => l.level === filter)

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-sm">
          <FileWarning className="h-4 w-4" />
          Error Log
          {loading && <Loader2 className="h-3 w-3 animate-spin text-text-muted" />}
          <div className="ml-auto flex gap-1">
            {['all', 'critical', 'error', 'warning'].map((f) => (
              <button key={f} onClick={() => setFilter(f)}
                className={cn('px-2 py-0.5 text-[10px] rounded cursor-pointer', filter === f ? 'bg-accent/10 text-accent' : 'text-text-muted hover:text-text-primary')}>
                {f}
              </button>
            ))}
          </div>
          <button onClick={fetchLog} className="text-text-muted hover:text-text-primary cursor-pointer"><RefreshCw className="h-3.5 w-3.5" /></button>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="max-h-64 overflow-y-auto space-y-0.5 font-mono text-[11px]">
          {filtered.map((entry, i) => (
            <div key={i} className={cn(
              'px-2 py-1 rounded',
              entry.level === 'critical' ? 'bg-danger/10 text-danger' :
              entry.level === 'error' ? 'bg-danger/5 text-danger/80' :
              entry.level === 'warning' ? 'bg-warning/10 text-warning' :
              'text-text-muted'
            )}>
              {entry.line}
            </div>
          ))}
          {filtered.length === 0 && !loading && <p className="text-sm text-text-muted text-center py-4">No log entries</p>}
        </div>
      </CardContent>
    </Card>
  )
}

// ─── Row helper ─────────────────────────────────────────────────────────
function Row({ label, value, icon, small }: { label: string; value: string; icon?: React.ReactNode; small?: boolean }) {
  return (
    <div className={cn('flex items-center justify-between', small ? 'text-xs' : 'text-sm')}>
      <span className="text-text-muted">{label}</span>
      <span className="flex items-center gap-1.5 text-text-primary">{icon}{value}</span>
    </div>
  )
}

// ─── Site Paused (soft-disconnected, credential still stored) ──────────
function SitePaused({ site, onReconnected, onRemoved }: { site: WpSiteMeta; onReconnected: () => void; onRemoved: () => void }) {
  const [reconnecting, setReconnecting] = useState(false)
  const [confirmingRemove, setConfirmingRemove] = useState(false)
  const [error, setError] = useState('')

  const handleReconnect = async () => {
    setReconnecting(true); setError('')
    try {
      const res = await fetch(`${API_BASE}/sites/${site.site_id}/reconnect`, { method: 'POST', headers: apiHeaders() })
      const data = await res.json()
      if (!res.ok || data.success === false) { setError(data.error || 'Reconnect failed'); setReconnecting(false); return }
      onReconnected()
    } catch {
      setError('Could not reconnect.')
      setReconnecting(false)
    }
  }

  return (
    <div className="flex flex-col items-center justify-center py-12 space-y-6 max-w-xl mx-auto text-center">
      <Globe className="h-16 w-16 text-text-muted/40" />
      <div className="space-y-2">
        <h2 className="text-xl font-bold text-text-primary">{site.name} Disconnected</h2>
        <p className="text-text-muted text-sm">
          The stored credential for {site.url} is still saved -- reconnect any time without re-entering it,
          or remove it permanently if you're done with this site.
        </p>
      </div>
      {error && <p className="text-xs text-danger">{error}</p>}
      {!confirmingRemove ? (
        <div className="flex gap-3">
          <button onClick={handleReconnect} disabled={reconnecting}
            className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium bg-[#21759b] text-white rounded-lg hover:bg-[#1a5f7e] transition-colors cursor-pointer disabled:opacity-50">
            {reconnecting ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Reconnect
          </button>
          <button onClick={() => setConfirmingRemove(true)}
            className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium border border-danger/30 text-danger rounded-lg hover:bg-danger/10 transition-colors cursor-pointer">
            <Trash2 className="h-4 w-4" />
            Remove Credential
          </button>
        </div>
      ) : (
        <DeleteConfirmModal site={site} onDone={onRemoved} onCancel={() => setConfirmingRemove(false)} />
      )}
    </div>
  )
}

// ─── Connected Dashboard ────────────────────────────────────────────
function ConnectedDashboard({ sites, onAddSite, onRefresh }: { sites: WpSiteMeta[]; onAddSite: () => void; onRefresh: () => void }) {
  const [activeSiteId, setActiveSiteId] = useState(sites[0]?.site_id || '')
  const [editingCreds, setEditingCreds] = useState(false)
  const [disconnecting, setDisconnecting] = useState(false)
  const [testingConn, setTestingConn] = useState(false)
  const [testResult, setTestResult] = useState<string | null>(null)
  const activeSite = sites.find((s) => s.site_id === activeSiteId) || sites[0]

  useEffect(() => {
    if (!sites.find((s) => s.site_id === activeSiteId) && sites.length > 0) {
      setActiveSiteId(sites[0].site_id)
    }
  }, [sites, activeSiteId])

  if (!activeSite) return null

  const handleTestConnection = async () => {
    setTestingConn(true); setTestResult(null)
    try {
      const res = await fetch(`${API_BASE}/sites/${activeSite.site_id}/health`, { headers: apiHeaders() })
      setTestResult(res.ok ? 'connected' : 'failed')
    } catch { setTestResult('failed') }
    setTestingConn(false)
  }

  const handleDisconnect = async () => {
    setDisconnecting(true)
    try { await fetch(`${API_BASE}/sites/${activeSite.site_id}/disconnect`, { method: 'POST', headers: apiHeaders() }) } finally { setDisconnecting(false); onRefresh() }
  }

  const isDisconnected = activeSite.status === 'disconnected'
  // Three real states, not two: green when genuinely connected and no
  // recent test has failed; amber when connected but the last live test
  // came back failed (a real connection issue, not a full disconnect);
  // red only when actually disconnected. Per direct instruction.
  const connectionState: 'green' | 'amber' | 'red' = isDisconnected ? 'red' : testResult === 'failed' ? 'amber' : 'green'

  return (
    <div className="ops-wp-dashboard space-y-4">
      <section className="ops-wp-site-summary">
        <div className="ops-wp-site-main">
          <span className="ops-wp-logo" aria-hidden="true">W</span>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-lg font-bold text-text-primary truncate">{activeSite.name}</h2>
              <span className={cn('ops-wp-connection-badge',
                connectionState === 'red' ? 'is-red' : connectionState === 'amber' ? 'is-amber' : 'is-green'
              )}>
                <span className="w-2 h-2 rounded-full bg-current" />
                {isDisconnected ? 'Disconnected' : connectionState === 'amber' ? 'Connection issue' : 'Connected'}
              </span>
            </div>
            <p className="text-xs text-text-muted mt-0.5">WordPress</p>
            <p className="text-[11px] text-text-muted truncate mt-0.5">{activeSite.url}</p>
          </div>
          {sites.length > 1 && (
            <select value={activeSiteId} onChange={(e) => { setActiveSiteId(e.target.value); setEditingCreds(false); setTestResult(null) }}
              className="ops-wp-site-select">
              {sites.map((site) => <option key={site.site_id} value={site.site_id}>{site.name}{site.primary ? ' (primary)' : ''}{site.status === 'disconnected' ? ' (disconnected)' : ''}</option>)}
            </select>
          )}
        </div>

        {!isDisconnected && (
          <div className="ops-wp-actions">
            <button onClick={handleTestConnection} disabled={testingConn} className="ops-wp-action">
              {testingConn ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              <span>Test Connection</span>
            </button>
            <button onClick={() => setEditingCreds(!editingCreds)} className="ops-wp-action">
              <Pencil className="h-4 w-4" />
              <span>Credentials</span>
            </button>
            {sites.length < 5 && (
              <button onClick={onAddSite} className="ops-wp-action">
                <Globe className="h-4 w-4" />
                <span>Add Site</span>
              </button>
            )}
            <button onClick={handleDisconnect} disabled={disconnecting} className="ops-wp-action ops-wp-action-danger">
              {disconnecting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plug className="h-4 w-4" />}
              <span>Disconnect</span>
            </button>
          </div>
        )}
        {testResult && !isDisconnected && (
          <Badge variant={testResult === 'connected' ? 'success' : 'danger'} className="mt-3 text-xs">
            {testResult === 'connected' ? 'Connection test passed' : 'Connection test failed'}
          </Badge>
        )}
      </section>

      {isDisconnected ? (
        <SitePaused site={activeSite} onReconnected={onRefresh} onRemoved={onRefresh} />
      ) : (
        <>
          {editingCreds && <UpdateCredentialsModal site={activeSite} onDone={() => { setEditingCreds(false); onRefresh() }} />}
          <div className="ops-wp-overview-grid">
            <SiteHealthCard site={activeSite} />
            <DatabaseCard site={activeSite} />
          </div>
          <PluginsCard site={activeSite} />
          <ErrorLogCard site={activeSite} />
        </>
      )}
    </div>
  )
}

// ─── Deep Connection (ops-wordpress-connection, AD-033) ────────────────
const DEEP_API_BASE = '/api/dashboard/wordpress-deep'

interface DeepSiteInfo {
  connected: boolean
  has_credential?: boolean
  status?: 'active' | 'disconnected' | 'unknown' | 'none'
  site_url?: string
  title?: string
  active_theme?: string
  active_plugins_count?: number
  active_plugins?: Array<{ name: string; version: string }>
  disconnected_at?: string
}

// ─── Discovery Engine capability candidates (AD-050) ────────────────
// Additive to the existing Deep Connection tab, not a new tab -- see
// AD-050's own reasoning: every candidate here was gathered through the
// exact same plugin-MCP connection this tab's own token already
// establishes. Deliberately not an automated verdict -- every finding
// keeps its own confidence and evidence_status, never collapsed into
// one score, and nothing here can turn into a live tool without an
// explicit human decision recorded through the actions below.
interface CandidateEvidence {
  finding: string
  result?: string
  method?: string | null
  evidence_status?: 'confirmed' | 'partially_confirmed' | 'inferred' | 'unknown'
  confidence?: 'high' | 'medium' | 'low' | 'none'
  limitations?: string[]
  observed_at?: string
}

interface CapabilityCandidate {
  candidate_id: string
  tenant_id: string
  site_id?: string | null
  plugin_slug: string
  discovered_capability: string
  proposed_tool: string
  proposed_by?: string
  created_at: string
  updated_at: string
  status: 'awaiting_review' | 'approved' | 'rejected' | 'needs_investigation'
  evidence: CandidateEvidence[]
  scan_metadata?: { plugin_scan_capped?: boolean; files_scanned?: number } | null
  missing_evidence?: string[]
  risk_tier_suggested?: number | null
  blocking_evidence?: string[]
  review_note?: string
  rejection_reason?: string
}

function CandidateStatusBadge({ status }: { status: CapabilityCandidate['status'] }) {
  if (status === 'approved') return <Badge variant="success" className="text-[10px]">Approved</Badge>
  if (status === 'rejected') return <Badge variant="danger" className="text-[10px]">Rejected</Badge>
  if (status === 'needs_investigation') return <Badge variant="warning" className="text-[10px]">Needs investigation</Badge>
  return <Badge variant="outline" className="text-[10px]">Awaiting review</Badge>
}

function ConfidenceDot({ confidence }: { confidence?: string }) {
  const color = confidence === 'high' ? 'bg-success' : confidence === 'medium' ? 'bg-warning' : confidence === 'low' ? 'bg-orange-400' : 'bg-text-muted/40'
  return <span className={cn('inline-block w-1.5 h-1.5 rounded-full shrink-0', color)} title={confidence || 'none'} />
}

function CandidateCard({ candidate, onUpdated }: { candidate: CapabilityCandidate; onUpdated: () => void }) {
  const [expanded, setExpanded] = useState(false)
  const [updating, setUpdating] = useState(false)
  const [error, setError] = useState('')

  const updateStatus = async (status: CapabilityCandidate['status'], extra?: { review_note?: string; rejection_reason?: string }) => {
    setUpdating(true); setError('')
    try {
      const res = await fetch(`${DEEP_API_BASE}/candidates`, {
        method: 'PATCH',
        headers: apiHeaders(),
        body: JSON.stringify({ candidate_id: candidate.candidate_id, status, ...extra }),
      })
      if (!res.ok) { setError('Update failed -- try again.'); setUpdating(false); return }
      onUpdated()
    } catch {
      setError('Could not update this candidate.')
    } finally { setUpdating(false) }
  }

  const blockingCount = candidate.blocking_evidence?.length ?? 0

  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <button onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-surface-elevated transition-colors cursor-pointer">
        {expanded ? <ChevronUp className="h-4 w-4 text-text-muted shrink-0" /> : <ChevronDown className="h-4 w-4 text-text-muted shrink-0" />}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-text-primary truncate">{candidate.proposed_tool}</span>
            <CandidateStatusBadge status={candidate.status} />
            {blockingCount > 0 && (
              <span className="text-[10px] text-text-muted">{blockingCount} open question{blockingCount === 1 ? '' : 's'}</span>
            )}
          </div>
          <p className="text-xs text-text-muted mt-0.5 font-mono">{candidate.plugin_slug}</p>
        </div>
        <span className="text-[10px] text-text-muted shrink-0">{candidate.proposed_by || 'claude'}</span>
      </button>

      {expanded && (
        <div className="px-4 pb-4 space-y-3 border-t border-border pt-3">
          <p className="text-xs text-text-muted leading-relaxed">{candidate.discovered_capability}</p>

          <div className="space-y-1.5">
            <p className="text-[10px] uppercase tracking-wider text-text-muted font-medium">Evidence</p>
            {candidate.evidence.map((e, i) => (
              <div key={i} className="flex items-start gap-2 text-xs py-1 border-b border-border last:border-0">
                <ConfidenceDot confidence={e.confidence} />
                <div className="min-w-0 flex-1">
                  <span className="text-text-primary">{e.finding}</span>
                  <span className="text-text-muted"> — {e.result || 'no result'}{e.method ? ` (${e.method})` : ''}</span>
                  {e.limitations && e.limitations.length > 0 && (
                    <p className="text-[10px] text-text-muted/80 mt-0.5">{e.limitations.join('; ')}</p>
                  )}
                </div>
                <span className="text-[9px] text-text-muted shrink-0 font-mono">{e.evidence_status || 'unknown'}</span>
              </div>
            ))}
          </div>

          {candidate.missing_evidence && candidate.missing_evidence.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-text-muted font-medium mb-1">Missing evidence</p>
              <ul className="space-y-0.5">
                {candidate.missing_evidence.map((m, i) => (
                  <li key={i} className="text-xs text-text-muted flex items-start gap-1.5">
                    <span className="shrink-0">•</span><span>{m}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {candidate.risk_tier_suggested != null && (
            <p className="text-xs text-text-muted">Suggested tier: <span className="text-text-primary font-medium">{candidate.risk_tier_suggested}</span></p>
          )}

          {candidate.review_note && (
            <p className="text-xs text-text-muted italic">"{candidate.review_note}"</p>
          )}

          {error && <p className="text-xs text-danger">{error}</p>}

          {candidate.status !== 'approved' && candidate.status !== 'rejected' && (
            <div className="flex gap-2 flex-wrap pt-1">
              <button onClick={() => updateStatus('approved')} disabled={updating}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-success/10 text-success rounded hover:bg-success/20 transition-colors cursor-pointer disabled:opacity-50">
                <CheckCircle2 className="h-3.5 w-3.5" />
                Approve
              </button>
              <button onClick={() => updateStatus('needs_investigation')} disabled={updating}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-warning/10 text-warning rounded hover:bg-warning/20 transition-colors cursor-pointer disabled:opacity-50">
                <HelpCircle className="h-3.5 w-3.5" />
                Investigate further
              </button>
              <button onClick={() => updateStatus('rejected')} disabled={updating}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-danger/10 text-danger rounded hover:bg-danger/20 transition-colors cursor-pointer disabled:opacity-50">
                <XCircle className="h-3.5 w-3.5" />
                Reject
              </button>
              {updating && <Loader2 className="h-3.5 w-3.5 animate-spin text-text-muted self-center" />}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function CapabilityCandidatesCard() {
  const [candidates, setCandidates] = useState<CapabilityCandidate[]>([])
  const [loading, setLoading] = useState(false)
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const globalRefreshVersion = useAppStore((s) => s.globalRefreshVersion)

  const fetchCandidates = useCallback(async () => {
    setLoading(true)
    try {
      const qs = statusFilter !== 'all' ? `?status=${statusFilter}` : ''
      const res = await fetch(`${DEEP_API_BASE}/candidates${qs}`, { headers: apiHeaders() })
      if (res.ok) { const d = await res.json(); setCandidates(d.candidates || []) }
    } catch { /* ignore */ }
    setLoading(false)
  }, [statusFilter])

  useEffect(() => { fetchCandidates() }, [fetchCandidates, globalRefreshVersion])

  return (
    <Card className="ops-wp-card">
      <CardHeader className="ops-wp-card-header">
        <CardTitle className="flex items-center gap-2 text-sm flex-wrap">
          <span className="ops-wp-section-icon"><Sparkles className="h-4 w-4" /></span>
          Discovery Candidates
          {loading && <Loader2 className="h-3 w-3 animate-spin text-text-muted" />}
          <div className="ml-auto flex gap-1">
            {['all', 'awaiting_review', 'needs_investigation', 'approved', 'rejected'].map((f) => (
              <button key={f} onClick={() => setStatusFilter(f)}
                className={cn('px-2 py-0.5 text-[10px] rounded cursor-pointer whitespace-nowrap', statusFilter === f ? 'bg-accent/10 text-accent' : 'text-text-muted hover:text-text-primary')}>
                {f === 'all' ? 'all' : f.replace('_', ' ')}
              </button>
            ))}
          </div>
          <button onClick={fetchCandidates} className="text-text-muted hover:text-text-primary cursor-pointer shrink-0"><RefreshCw className="h-3.5 w-3.5" /></button>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-xs text-text-muted mb-3">
          Capability candidates the AI has proposed after discovering and testing plugin behaviour on this site --
          each one carries its own evidence, not a single verdict. Nothing here becomes a live tool without your review.
        </p>
        <div className="space-y-2">
          {candidates.map((c) => (
            <CandidateCard key={c.candidate_id} candidate={c} onUpdated={fetchCandidates} />
          ))}
          {candidates.length === 0 && !loading && (
            <p className="text-sm text-text-muted text-center py-4">No candidates {statusFilter !== 'all' ? `with status "${statusFilter.replace('_', ' ')}"` : 'proposed yet'}.</p>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

function DeepConnectionForm({ siteId, siteUrl, onSuccess, onCancel }: { siteId: string; siteUrl: string; onSuccess: () => void; onCancel: () => void }) {
  const [mcpToken, setMcpToken] = useState('')
  const [showToken, setShowToken] = useState(false)
  const [testing, setTesting] = useState(false)
  const [error, setError] = useState('')
  const [pluginNote, setPluginNote] = useState('')

  const handleConnect = async () => {
    setError(''); setPluginNote('')
    if (!mcpToken) { setError('The OpsBridge Deep Token is required'); return }
    setTesting(true)
    try {
      const res = await fetch(`${DEEP_API_BASE}/sites/${siteId}/connect`, {
        method: 'POST',
        headers: apiHeaders(),
        body: JSON.stringify({ url: siteUrl, mcp_token: mcpToken }),
      })
      const data = await res.json()
      if (!res.ok || data.success === false) { setError(data.error || 'Connection failed'); setTesting(false); return }
      if (data.plugin_note) setPluginNote(data.plugin_note)
      onSuccess()
    } catch {
      setError('Could not connect. Check the Deep Token.')
    } finally { setTesting(false) }
  }

  return (
    <Card className="ops-wp-card max-w-lg mx-auto">
      <CardHeader className="ops-wp-card-header">
        <CardTitle className="flex items-center gap-2 text-sm">
          <span className="ops-wp-section-icon"><Shield className="h-4 w-4" /></span>
          Deep Connection -- More Exposure &amp; Control
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-text-muted">Same site, a second connection with post/page create and edit
          capability, plus deeper diagnostics via the OpsBridge Connector plugin. Independent of the
          Health Check connection above -- connecting here doesn't affect it. The plugin's own token is
          sufficient on its own -- no need to re-enter your Application Password here.</p>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-text-muted block mb-1">Site</label>
            {/* Real fix 2026-09-01, per direct instruction: the site was
                already chosen on the selector above -- typing its URL
                again here was pure duplication. Read-only, not editable,
                since changing the target site means picking a different
                one above, not editing this field. */}
            <input value={siteUrl} disabled readOnly
              className="w-full px-3 py-2 text-sm bg-surface border border-border rounded-lg text-text-muted cursor-not-allowed" />
          </div>
          <div>
            <label className="text-xs text-text-muted block mb-1">OpsBridge Deep Token</label>
            <div className="relative">
              <input value={mcpToken} onChange={(e) => setMcpToken(e.target.value)} type={showToken ? 'text' : 'password'} placeholder="Paste from the plugin's Settings page"
                className="w-full px-3 py-2 pr-10 text-sm bg-surface-elevated border border-border rounded-lg text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent" />
              <button type="button" onClick={() => setShowToken(!showToken)} className="absolute right-2 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-primary cursor-pointer">
                {showToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <p className="text-[10px] text-text-muted mt-1">Settings &rarr; OpsBridge Connector in wp-admin -- unlocks WCFM
              marketplace data (membership plans, payouts, vendor groups) as well as deeper diagnostics.</p>
          </div>
        </div>
        {error && <p className="text-xs text-danger">{error}</p>}
        {pluginNote && <p className="text-xs text-text-muted">{pluginNote}</p>}
        <div className="flex gap-2">
          <button onClick={handleConnect} disabled={testing}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium bg-accent text-white rounded-lg hover:bg-accent/90 disabled:opacity-50 transition-colors cursor-pointer">
            {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
            {testing ? 'Testing connection…' : 'Connect'}
          </button>
          <button onClick={onCancel} className="px-4 py-2 text-sm text-text-muted hover:text-text-primary transition-colors cursor-pointer">Cancel</button>
        </div>
      </CardContent>
    </Card>
  )
}

// ─── Browser Extension Remote Control (AD-055) ──────────────────────────
// Genuinely separate credential and purpose from the Deep Connection's own
// MCP token above -- this one authorizes a real, persistent Chrome browser
// (extension-driven, running on-demand on the VPS) to remotely view/operate
// a connected WordPress site, not MCP tool calls. Persistently visible
// alongside the Deep Connection status, matching the same always-on
// connect/disconnect pattern the simpler Health Check connection above
// uses, rather than tucked inside a setup form. The stored key here is for
// dashboard display/reference only -- the extension's own connection to
// exec-bridge authenticates live, directly against the WordPress site's
// own /verify-extension-key endpoint, every time, not against this copy.
function ExtensionRemoteControlBar({ siteId }: { siteId: string }) {
  return <BrowserPairingControl key={siteId} siteId={siteId} legacy={<LegacyExtensionRemoteControlBar siteId={siteId} />} />
}

function LegacyExtensionRemoteControlBar({ siteId }: { siteId: string }) {
  const [connected, setConnected] = useState(false)
  const [connectionKey, setConnectionKey] = useState('')
  const [saving, setSaving] = useState(false)
  const [disconnecting, setDisconnecting] = useState(false)
  const [error, setError] = useState('')

  // Real fix 2026-09-01, per direct instruction: this component previously
  // saved default_site_id as a raw site URL to a flat, un-scoped endpoint,
  // and ran its own separate site-selector logic despite already living
  // inside DeepConnectionDashboard, which already knows exactly which
  // site it's on. Now takes siteId directly from its parent, same pattern
  // as Deep Connection and Backend Access got earlier tonight -- no
  // separate site list, no separate default-picking logic needed here.
  const extApiBase = `${DEEP_API_BASE}/sites/${siteId}/extension-config`

  const fetchStatus = useCallback(async () => {
    try {
      const res = await fetch(extApiBase, { headers: apiHeaders() })
      if (res.ok) { const d = await res.json(); setConnected(!!d.connected) }
    } catch { /* ignore */ }
  }, [extApiBase])

  useEffect(() => { fetchStatus() }, [fetchStatus])

  const handleSave = async () => {
    setError('')
    if (!connectionKey) { setError('Paste the connection key from the plugin\'s Settings page first.'); return }
    setSaving(true)
    try {
      const res = await fetch(extApiBase, {
        method: 'POST',
        headers: apiHeaders(),
        body: JSON.stringify({ connection_key: connectionKey }),
      })
      const data = await res.json()
      if (!res.ok || data.success === false) { setError(data.error || 'Save failed'); setSaving(false); return }
      setConnected(true)
      setConnectionKey('')
    } catch {
      setError('Could not save.')
    } finally { setSaving(false) }
  }

  const handleDisconnect = async () => {
    setDisconnecting(true)
    try {
      await fetch(extApiBase, { method: 'DELETE', headers: apiHeaders() })
      setConnected(false)
      setConnectionKey('')
    } finally { setDisconnecting(false) }
  }

  return (
    <Card className="ops-wp-card">
      <CardHeader className="ops-wp-card-header">
        <CardTitle className="flex items-center gap-2 text-sm flex-wrap">
          <span className="ops-wp-section-icon"><MousePointerClick className="h-4 w-4" /></span>
          Browser Remote Control
          <span className={cn('inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded-full',
            connected ? 'bg-success/10 text-success' : 'bg-text-muted/10 text-text-muted'
          )}>
            <span className={cn('w-1.5 h-1.5 rounded-full', connected ? 'bg-success' : 'bg-text-muted/40')} />
            {connected ? 'connected' : 'not connected'}
          </span>
          {/* Connect or disconnect only -- deliberately no edit path, per
              direct instruction: once a key is saved, it's never editable
              or re-viewable through the dashboard again. Changing it means
              disconnecting first, then connecting fresh with a new key --
              the same discipline as the value never being readable back
              on the backend side either (see runGetExtensionConfig). */}
          {connected && (
            <div className="ml-auto">
              <button onClick={handleDisconnect} disabled={disconnecting} className="ops-wp-action ops-wp-action-danger">
                {disconnecting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plug className="h-3 w-3" />}
                Disconnect
              </button>
            </div>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-text-muted">Lets the AI remotely view and operate a real, persistent browser session on
          this site -- separate from the MCP token above. Generate a connection key from the plugin's own Settings page
          first, then paste it here.</p>
        {!connected && (
          <>
            <div>
              <label className="text-xs text-text-muted block mb-1">Connection key</label>
              <div className="relative max-w-md">
                <input value={connectionKey} onChange={(e) => setConnectionKey(e.target.value)} type="password"
                  placeholder="Paste from the plugin's Settings page"
                  className="w-full px-3 py-2 text-sm bg-surface-elevated border border-border rounded-lg text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent" />
              </div>
            </div>
            {error && <p className="text-xs text-danger">{error}</p>}
            <div className="flex gap-2">
              <button onClick={handleSave} disabled={saving}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-accent text-white rounded-lg hover:bg-accent/90 disabled:opacity-50 transition-colors cursor-pointer">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
                {saving ? 'Saving…' : 'Connect'}
              </button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}

function DeepConnectionDashboard({ siteId, siteInfo, onDisconnect }: { siteId: string; siteInfo: DeepSiteInfo; onDisconnect: () => void }) {
  const [pluginDetected, setPluginDetected] = useState(false)
  const [disconnecting, setDisconnecting] = useState(false)
  const globalRefreshVersion = useAppStore((s) => s.globalRefreshVersion)

  // Deliberately not duplicating diagnostics/DB health/error log here --
  // that's the Health Check tab's own job (real data, pulled directly by
  // the plugin, shown once, in one place), per direct instruction. This
  // tab only needs to know whether the plugin is present at all, to
  // decide whether to show the plugin-dependent Capability Candidates
  // section below.
  const fetchPluginStatus = useCallback(async () => {
    try { const r = await fetch(`${DEEP_API_BASE}/sites/${siteId}/health`, { headers: apiHeaders() }); if (r.ok) { const d = await r.json(); setPluginDetected(!!d.plugin_detected) } } catch { /* ignore */ }
  }, [siteId])

  useEffect(() => { fetchPluginStatus() }, [fetchPluginStatus, globalRefreshVersion])

  const handleDisconnect = async () => {
    setDisconnecting(true)
    try { await fetch(`${DEEP_API_BASE}/sites/${siteId}/disconnect`, { method: 'POST', headers: apiHeaders() }) } finally { setDisconnecting(false); onDisconnect() }
  }

  return (
    <div className="ops-wp-deep-dashboard space-y-4">
      <section className="ops-wp-site-summary">
        <div className="ops-wp-site-main">
          <span className="ops-wp-section-icon" aria-hidden="true"><Shield className="h-5 w-5" /></span>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-lg font-bold text-text-primary">Deep Connection</h2>
              <span className="ops-wp-connection-badge is-green"><span className="w-2 h-2 rounded-full bg-current" />Connected</span>
            </div>
            <p className="text-xs text-text-muted mt-1 truncate">{siteInfo.site_url}</p>
          </div>
        </div>
        <div className="ops-wp-deep-actions">
          <button onClick={handleDisconnect} disabled={disconnecting} className="ops-wp-action ops-wp-action-danger">
            {disconnecting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plug className="h-3 w-3" />}
            Disconnect
          </button>
        </div>
      </section>

      <ExtensionRemoteControlBar siteId={siteId} />

      <Card className="ops-wp-card">
        <CardContent className="pt-4">
          <Row label="OpsBridge Connector plugin" value={pluginDetected ? 'Detected' : 'Not detected'}
            icon={pluginDetected ? <StatusDot ok /> : <StatusDot ok={false} />} />
          {!pluginDetected && (
            <p className="text-[11px] text-text-muted pt-1">Post/page create and edit already works without the
              plugin. Install it for the deeper diagnostics shown on the Health Check tab, plus everything below.</p>
          )}
        </CardContent>
      </Card>

      {pluginDetected && <CapabilityCandidatesCard />}
    </div>
  )
}

function DeepConnectionPaused({ siteId, siteInfo, onReconnected, onRemoved, onConnectNew }: { siteId: string; siteInfo: DeepSiteInfo; onReconnected: () => void; onRemoved: () => void; onConnectNew: () => void }) {
  const [reconnecting, setReconnecting] = useState(false)
  const [removing, setRemoving] = useState(false)
  const [confirmRemove, setConfirmRemove] = useState(false)
  const [error, setError] = useState('')

  const handleReconnect = async () => {
    setReconnecting(true); setError('')
    try {
      const res = await fetch(`${DEEP_API_BASE}/sites/${siteId}/reconnect`, { method: 'POST', headers: apiHeaders() })
      const data = await res.json()
      if (!res.ok || data.success === false) { setError(data.error || 'Reconnect failed'); setReconnecting(false); return }
      onReconnected()
    } catch {
      setError('Could not reconnect.')
      setReconnecting(false)
    }
  }

  const handleRemove = async () => {
    setRemoving(true)
    try { await fetch(`${DEEP_API_BASE}/sites/${siteId}/remove-credential`, { method: 'POST', headers: apiHeaders() }) } finally { setRemoving(false); onRemoved() }
  }

  return (
    <div className="flex flex-col items-center justify-center py-12 space-y-6 max-w-xl mx-auto text-center">
      <Shield className="h-16 w-16 text-text-muted/40" />
      <div className="space-y-2">
        <h2 className="text-xl font-bold text-text-primary">Deep Connection Paused</h2>
        <p className="text-text-muted text-sm">
          {siteInfo.site_url} is disconnected but the credential is still stored securely -- reconnect any time
          without re-entering it, or remove it permanently if you're done with this site.
        </p>
        {/* Real fix 2026-09-01: Reconnect only ever re-activates the
            already-stored token as-is -- it has no way to accept a
            replacement. If the token was regenerated on the plugin's own
            Settings page since this was disconnected (which commonly
            invalidates the old one), Reconnect will silently "succeed" at
            the storage level while the token itself no longer works. This
            note, and the new button below, exist specifically for that
            case. */}
        <p className="text-[11px] text-text-muted">Generated a new token on the plugin's own Settings page since
          disconnecting? Reconnect re-activates the old one, which may no longer work -- use "Connect with a new
          token" instead.</p>
      </div>
      {error && <p className="text-xs text-danger">{error}</p>}
      <div className="flex gap-3 flex-wrap justify-center">
        <button onClick={handleReconnect} disabled={reconnecting}
          className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium bg-[#21759b] text-white rounded-lg hover:bg-[#1a5f7e] transition-colors cursor-pointer disabled:opacity-50">
          {reconnecting ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          Reconnect
        </button>
        <button onClick={onConnectNew}
          className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium border border-border text-text-primary rounded-lg hover:bg-surface-elevated transition-colors cursor-pointer">
          <KeyRound className="h-4 w-4" />
          Connect with a new token
        </button>
        {!confirmRemove ? (
          <button onClick={() => setConfirmRemove(true)}
            className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium border border-danger/30 text-danger rounded-lg hover:bg-danger/10 transition-colors cursor-pointer">
            <Trash2 className="h-4 w-4" />
            Remove Credential
          </button>
        ) : (
          <button onClick={handleRemove} disabled={removing}
            className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium bg-danger text-white rounded-lg hover:bg-danger/90 transition-colors cursor-pointer disabled:opacity-50">
            {removing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
            Confirm Remove
          </button>
        )}
      </div>
    </div>
  )
}


function BackendAccessTab({ siteId, siteUrl: knownSiteUrl }: { siteId: string; siteUrl?: string }) {
  const [connected, setConnected] = useState(false)
  const [siteUrl, setSiteUrl] = useState(knownSiteUrl || '')
  const [host, setHost] = useState('')
  const [port, setPort] = useState('22')
  const [username, setUsername] = useState('')
  const [authType, setAuthType] = useState<'password' | 'key'>('key')
  const [password, setPassword] = useState('')
  const [privateKey, setPrivateKey] = useState('')
  const [statusSiteUrl, setStatusSiteUrl] = useState('')
  const [statusAuthType, setStatusAuthType] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [disconnecting, setDisconnecting] = useState(false)
  const [error, setError] = useState('')

  const backendApiBase = `${DEEP_API_BASE}/sites/${siteId}/backend-access`

  const fetchStatus = useCallback(async () => {
    try {
      const res = await fetch(backendApiBase, { headers: apiHeaders() })
      if (res.ok) {
        const d = await res.json()
        setConnected(!!d.connected)
        if (d.connected) {
          setStatusSiteUrl(d.site_url || '')
          setStatusAuthType(d.auth_type || '')
        }
      }
    } catch { /* ignore */ }
    setLoading(false)
  }, [backendApiBase])

  useEffect(() => { fetchStatus() }, [fetchStatus])

  const handleSave = async () => {
    setError('')
    if (!siteUrl) { setError('Website domain is required.'); return }
    if (!host || !username) { setError('Host and username are required.'); return }
    if (authType === 'password' && !password) { setError('Password is required.'); return }
    if (authType === 'key' && !privateKey) { setError('Private key is required.'); return }
    setSaving(true)
    try {
      const res = await fetch(backendApiBase, {
        method: 'POST',
        headers: apiHeaders(),
        body: JSON.stringify({
          site_url: siteUrl, host, port: Number(port) || 22, username, auth_type: authType,
          password: authType === 'password' ? password : undefined,
          private_key: authType === 'key' ? privateKey : undefined,
        }),
      })
      const data = await res.json()
      if (!res.ok || data.success === false) { setError(data.error || 'Save failed'); setSaving(false); return }
      setConnected(true)
      setPassword(''); setPrivateKey('')
      fetchStatus()
    } catch {
      setError('Could not save.')
    } finally { setSaving(false) }
  }

  const handleDisconnect = async () => {
    setDisconnecting(true)
    try {
      await fetch(backendApiBase, { method: 'DELETE', headers: apiHeaders() })
      setConnected(false)
      setHost(''); setUsername(''); setPassword(''); setPrivateKey('')
    } finally { setDisconnecting(false) }
  }

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-text-muted" /></div>

  // Corrected 2026-09-01: the warning and description that used to live
  // here now live in BackendAccessTabWithSelector instead, shown
  // alongside the site selector rather than only appearing after a site
  // is chosen -- kept out of this component to avoid the exact same
  // warning appearing twice on the same screen.
  return (
    <div className="ops-wp-backend-content max-w-3xl mx-auto space-y-4">
      <Card className="ops-wp-card">
        <CardHeader className="ops-wp-card-header">
          <CardTitle className="flex items-center gap-2 text-sm flex-wrap">
            <span className="ops-wp-section-icon"><Server className="h-4 w-4" /></span>
            Universal Backend Access
            <span className={cn('inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded-full',
              connected ? 'bg-success/10 text-success' : 'bg-text-muted/10 text-text-muted'
            )}>
              <span className={cn('w-1.5 h-1.5 rounded-full', connected ? 'bg-success' : 'bg-text-muted/40')} />
              {connected ? 'connected' : 'not connected'}
            </span>
            {/* Connect or disconnect only -- no edit, no reveal, matching
                Browser Remote Control's own pattern exactly. The credential
                is never readable back through the dashboard once saved. */}
            {connected && (
              <div className="ml-auto">
                <button onClick={handleDisconnect} disabled={disconnecting}
                  className="ops-wp-action ops-wp-action-danger">
                  {disconnecting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plug className="h-3 w-3" />}
                  Disconnect
                </button>
              </div>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {connected ? (
            <p className="text-xs text-text-muted">Connected to <span className="text-text-primary font-mono">{statusSiteUrl}</span> ({statusAuthType === 'key' ? 'private key' : 'password'} auth).</p>
          ) : (
            <>
              <div>
                <label className="text-xs text-text-muted block mb-1">Website domain</label>
                <input value={siteUrl} onChange={(e) => setSiteUrl(e.target.value)} placeholder="bakersguild.co.uk"
                  className="w-full px-3 py-2 text-sm bg-surface-elevated border border-border rounded-lg text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent" />
                <p className="text-[10px] text-text-muted mt-1">What shows on the dashboard -- kept separate from the real
                  server address below, so a compromised dashboard alone never reveals how to actually reach it.</p>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-2">
                  <label className="text-xs text-text-muted block mb-1">Host</label>
                  <input value={host} onChange={(e) => setHost(e.target.value)} placeholder="203.0.113.10 or yourserver.com"
                    className="w-full px-3 py-2 text-sm bg-surface-elevated border border-border rounded-lg text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent" />
                </div>
                <div>
                  <label className="text-xs text-text-muted block mb-1">Port</label>
                  <input value={port} onChange={(e) => setPort(e.target.value)} placeholder="22"
                    className="w-full px-3 py-2 text-sm bg-surface-elevated border border-border rounded-lg text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent" />
                </div>
              </div>
              <div>
                <label className="text-xs text-text-muted block mb-1">Username</label>
                <input value={username} onChange={(e) => setUsername(e.target.value)} placeholder="root"
                  className="w-full px-3 py-2 text-sm bg-surface-elevated border border-border rounded-lg text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent" />
              </div>
              <div>
                <label className="text-xs text-text-muted block mb-1">Authentication</label>
                <div className="flex gap-2">
                  <button type="button" onClick={() => setAuthType('key')}
                    className={cn('flex-1 px-3 py-2 text-xs font-medium rounded-lg border transition-colors cursor-pointer',
                      authType === 'key' ? 'border-accent text-accent bg-accent/5' : 'border-border text-text-muted hover:text-text-primary')}>
                    Private key
                  </button>
                  <button type="button" onClick={() => setAuthType('password')}
                    className={cn('flex-1 px-3 py-2 text-xs font-medium rounded-lg border transition-colors cursor-pointer',
                      authType === 'password' ? 'border-accent text-accent bg-accent/5' : 'border-border text-text-muted hover:text-text-primary')}>
                    Password
                  </button>
                </div>
              </div>
              {authType === 'key' ? (
                <div>
                  <label className="text-xs text-text-muted block mb-1">Private key</label>
                  <textarea value={privateKey} onChange={(e) => setPrivateKey(e.target.value)} rows={4}
                    placeholder="-----BEGIN OPENSSH PRIVATE KEY-----"
                    className="w-full px-3 py-2 text-sm font-mono bg-surface-elevated border border-border rounded-lg text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent" />
                </div>
              ) : (
                <div>
                  <label className="text-xs text-text-muted block mb-1">Password</label>
                  <input value={password} onChange={(e) => setPassword(e.target.value)} type="password"
                    className="w-full px-3 py-2 text-sm bg-surface-elevated border border-border rounded-lg text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent" />
                </div>
              )}
              {error && <p className="text-xs text-danger">{error}</p>}
              <button onClick={handleSave} disabled={saving}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-accent text-white rounded-lg hover:bg-accent/90 disabled:opacity-50 transition-colors cursor-pointer">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
                {saving ? 'Saving…' : 'Connect'}
              </button>
            </>
          )}
        </CardContent>
      </Card>

      <Card className="ops-wp-card mt-3">
        <CardContent>
          <h4 className="text-xs font-semibold text-text-primary mb-2">What backend access enables for your AI</h4>
          <p className="text-[10px] text-text-muted mb-2.5 leading-relaxed">When you connect your AI to OPS via MCP, backend access gives it the tools to go beyond detection — your AI can diagnose and resolve server-level issues directly.</p>
          <div className="space-y-2.5">
            <div>
              <p className="text-[11px] font-medium text-text-primary">WP-CLI Commands</p>
              <p className="text-[10px] text-text-muted leading-relaxed">Your AI can run bulk database operations, search-replace across the entire site, full exports and imports — with no timeout or memory limits.</p>
            </div>
            <div>
              <p className="text-[11px] font-medium text-text-primary">Direct MySQL Access</p>
              <p className="text-[10px] text-text-muted leading-relaxed">Your AI can run heavy queries, full database exports, index rebuilding, and complex joins — bypassing PHP memory constraints that limit what the plugin can do.</p>
            </div>
            <div>
              <p className="text-[11px] font-medium text-text-primary">File Permissions</p>
              <p className="text-[10px] text-text-muted leading-relaxed">Your AI can fix ownership issues, modify server configuration files, and resolve broken permissions after hosting migrations — changes PHP cannot make from inside WordPress.</p>
            </div>
            <div>
              <p className="text-[11px] font-medium text-text-primary">Server Diagnostics</p>
              <p className="text-[10px] text-text-muted leading-relaxed">Your AI can check disk usage, monitor processes, read error logs, and verify service health — the hosting environment around your site that the plugin cannot see.</p>
            </div>
            <div>
              <p className="text-[11px] font-medium text-text-primary">Performance Tuning</p>
              <p className="text-[10px] text-text-muted leading-relaxed">Your AI can configure OPcache, set up Redis or Memcached, tune PHP-FPM workers, and adjust MySQL caching — server-level optimisations that make the biggest difference.</p>
            </div>
            <div>
              <p className="text-[11px] font-medium text-text-primary">Automated Backup &amp; Recovery</p>
              <p className="text-[10px] text-text-muted leading-relaxed">Your AI can take full snapshots before making changes and restore automatically if something goes wrong — a safety net that works without waiting for a human.</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

function DeepConnectionTab({ siteId, siteUrl }: { siteId: string; siteUrl: string }) {
  const [siteInfo, setSiteInfo] = useState<DeepSiteInfo | null>(null)
  const [loading, setLoading] = useState(true)
  const [view, setView] = useState<'landing' | 'form' | 'dashboard' | 'paused'>('landing')
  const globalRefreshVersion = useAppStore((s) => s.globalRefreshVersion)

  const fetchStatus = useCallback(async () => {
    try {
      const res = await fetch(`${DEEP_API_BASE}/sites/${siteId}/status`, { headers: apiHeaders() })
      if (res.ok) {
        const data = await res.json()
        setSiteInfo(data)
        if (data.connected) setView('dashboard')
        else if (data.status === 'disconnected') setView('paused')
        else setView('landing')
      }
    } catch { /* ignore */ }
    setLoading(false)
  }, [siteId])

  useEffect(() => { fetchStatus() }, [fetchStatus, globalRefreshVersion])

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-text-muted" /></div>
  if (view === 'form') return <DeepConnectionForm siteId={siteId} siteUrl={siteUrl} onSuccess={fetchStatus} onCancel={() => setView(siteInfo?.connected ? 'dashboard' : siteInfo?.status === 'disconnected' ? 'paused' : 'landing')} />
  if (view === 'dashboard' && siteInfo) return <DeepConnectionDashboard siteId={siteId} siteInfo={siteInfo} onDisconnect={fetchStatus} />
  if (view === 'paused' && siteInfo) return <DeepConnectionPaused siteId={siteId} siteInfo={siteInfo} onReconnected={fetchStatus} onRemoved={fetchStatus} onConnectNew={() => setView('form')} />
  // Corrected 2026-09-01: the explanatory text that used to live here now
  // lives in DeepConnectionTabWithSelector instead, shown alongside the
  // site selector rather than only appearing after a site is chosen --
  // this landing view is simplified to just the Connect action, since the
  // caller already showed the explanation before rendering this component.
  return (
    <div className="flex flex-col items-center justify-center py-8">
      <button onClick={() => setView('form')}
        className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium bg-[#21759b] text-white rounded-lg hover:bg-[#1a5f7e] transition-colors cursor-pointer">
        Connect <ArrowRight className="h-4 w-4" />
      </button>
    </div>
  )
}

// ─── Download Plugin (built fresh from live source, never a stale release) ──
function DownloadPluginTab() {
  const [version, setVersion] = useState<string | null>(null)
  const [loadingVersion, setLoadingVersion] = useState(true)
  const globalRefreshVersion = useAppStore((s) => s.globalRefreshVersion)
  const setActiveSection = useAppStore((s) => s.setActiveSection)

  const fetchVersion = useCallback(async () => {
    setLoadingVersion(true)
    try {
      const res = await fetch(`${PLUGIN_API_BASE}/version`, { headers: apiHeaders() })
      if (res.ok) { const d = await res.json(); setVersion(d.version) }
    } catch { /* ignore */ }
    setLoadingVersion(false)
  }, [])

  useEffect(() => { fetchVersion() }, [fetchVersion, globalRefreshVersion])

  const openDownloadCenter = () => {
    sessionStorage.setItem('ops_download_focus', 'wordpress')
    setActiveSection('downloads')
  }

  return (
    <article className="ops-wp-download-card is-plugin">
      <div className="ops-wp-download-heading">
        <span className="ops-wp-download-icon" aria-hidden="true"><Download className="h-7 w-7" /></span>
        <div className="min-w-0">
          <h2 className="text-xl font-bold text-text-primary">OPS Connector</h2>
          <p className="text-text-muted text-sm leading-relaxed mt-2">
            The canonical plugin download now lives in Platform → Download Center. The package is still built fresh
            from the current GitHub source when you click Download there, so there is no duplicate release logic.
          </p>
        </div>
      </div>
      {!loadingVersion && version && <span className="ops-wp-version-pill">Current version: {version}</span>}
      <button onClick={openDownloadCenter} className="ops-wp-download-button is-plugin">
        <Package className="h-5 w-5" />
        Open Download Center
        <ArrowRight className="h-4 w-4" />
      </button>
      <div className="ops-wp-install-steps" aria-label="Plugin installation steps">
        {['Download Center', 'Upload', 'Activate'].map((step, index) => (
          <div key={step} className="ops-wp-install-step"><span>{index + 1}</span><small>{step}</small></div>
        ))}
      </div>
    </article>
  )
}

const EXTENSION_API_BASE = '/api/dashboard/extension'

function DownloadExtensionTab() {
  const [version, setVersion] = useState<string | null>(null)
  const [loadingVersion, setLoadingVersion] = useState(true)
  const globalRefreshVersion = useAppStore((s) => s.globalRefreshVersion)
  const setActiveSection = useAppStore((s) => s.setActiveSection)

  const fetchVersion = useCallback(async () => {
    setLoadingVersion(true)
    try {
      const res = await fetch(`${EXTENSION_API_BASE}/version`, { headers: apiHeaders() })
      if (res.ok) { const d = await res.json(); setVersion(d.version) }
    } catch { /* ignore */ }
    setLoadingVersion(false)
  }, [])

  useEffect(() => { fetchVersion() }, [fetchVersion, globalRefreshVersion])

  const openDownloadCenter = () => {
    sessionStorage.setItem('ops_download_focus', 'browser')
    setActiveSection('downloads')
  }

  return (
    <article className="ops-wp-download-card is-extension">
      <div className="ops-wp-download-heading">
        <span className="ops-wp-download-icon" aria-hidden="true"><MonitorSmartphone className="h-7 w-7" /></span>
        <div className="min-w-0">
          <h2 className="text-xl font-bold text-text-primary">OPS Universal Browser</h2>
          <p className="text-text-muted text-sm leading-relaxed mt-2">
            The browser extension is now an OPS-wide platform download rather than a WordPress-owned download.
            Open the Download Center for the current extension package, Native Input Helper and installation guidance.
          </p>
        </div>
      </div>
      {!loadingVersion && version && <span className="ops-wp-version-pill">Current version: {version}</span>}
      <button onClick={openDownloadCenter} className="ops-wp-download-button is-extension">
        <Package className="h-5 w-5" />
        Open Download Center
        <ArrowRight className="h-4 w-4" />
      </button>
      <p className="ops-wp-install-note">Platform → Download Center is now the canonical home for OPS software downloads.</p>
    </article>
  )
}

// ─── Site selectors for the top-level Deep Connection / Backend Access
// tabs ──────────────────────────────────────────────────────────────
// Restored as real top-level tabs per direct correction 2026-09-01: the
// earlier design nested these at the bottom of each site's Health row,
// which wasn't what was actually asked for -- the requirement was that
// the *gating logic* flows from Health (can't reach Deep Connection
// without a Health-connected site, can't reach SSH without a genuine
// Deep Connection for that same site), not that the controls themselves
// live buried inside a different tab. These wrappers pick a real,
// already-qualifying site first, then hand off to the already-fixed,
// siteId-aware DeepConnectionTab/BackendAccessTab underneath -- same
// components, same site-scoped API calls, just reached from their own
// top-level tab again with a real gate in front instead of an implicit
// one.
function DeepConnectionTabWithSelector({ sites }: { sites: WpSiteMeta[] }) {
  const [selectedSiteId, setSelectedSiteId] = useState('')

  useEffect(() => {
    // A single Health-connected site is used automatically, silently --
    // same "default exists, only prompt once genuinely ambiguous"
    // principle already used for Browser Remote Control's own site
    // selection below.
    if (sites.length === 1) setSelectedSiteId(sites[0].site_id)
    else if (selectedSiteId && !sites.find((s) => s.site_id === selectedSiteId)) setSelectedSiteId('')
  }, [sites, selectedSiteId])

  if (sites.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 space-y-4 max-w-xl mx-auto text-center">
        <Shield className="h-16 w-16 text-[#21759b]/40" />
        <div className="space-y-2">
          <h2 className="text-xl font-bold text-text-primary">Connect a site on Health Check first</h2>
          <p className="text-text-muted text-sm">Deep Connection requires a real, already-connected site to attach to --
            add one on the Health Check tab, then come back here.</p>
        </div>
      </div>
    )
  }

  // Corrected 2026-09-01, per direct instruction: the explanation of what
  // Deep Connection actually does must stay visible alongside the site
  // selector, not disappear until a site is chosen -- a customer needs to
  // see what this is before deciding which site to connect it to, not
  // after. This is the same explanation DeepConnectionTab's own landing
  // view used to show alone; it now lives here so it's never hidden by
  // the selector.
  return (
    <div className="ops-wp-deep-content max-w-3xl mx-auto space-y-4">
      {sites.length > 1 && (
        <div className="max-w-sm mx-auto">
          <label className="text-xs text-text-muted block mb-1">Which site?</label>
          <select value={selectedSiteId} onChange={(e) => setSelectedSiteId(e.target.value)}
            className="ops-wp-site-select w-full !ml-0">
            <option value="">Select a site…</option>
            {sites.map((s) => <option key={s.site_id} value={s.site_id}>{s.name}{s.primary ? ' (primary)' : ''}</option>)}
          </select>
        </div>
      )}
      {selectedSiteId ? <DeepConnectionTab siteId={selectedSiteId} siteUrl={sites.find((s) => s.site_id === selectedSiteId)?.url || ''} /> : sites.length > 1 ? (
        <p className="text-sm text-text-muted text-center py-4">Choose a site above to continue.</p>
      ) : null}
    </div>
  )
}

function BackendAccessTabWithSelector({ sites }: { sites: WpSiteMeta[] }) {
  const [deepConnectedIds, setDeepConnectedIds] = useState<string[] | null>(null)
  const [selectedSiteId, setSelectedSiteId] = useState('')
  const globalRefreshVersion = useAppStore((s) => s.globalRefreshVersion)

  // Real gate: SSH only ever shows sites that genuinely already have a
  // Deep Connection, checked directly per site rather than assumed --
  // "no SSH without a deeper connection first" enforced here by which
  // sites even appear as options, not by a separate permission check.
  useEffect(() => {
    let cancelled = false
    async function checkAll() {
      const results = await Promise.all(sites.map(async (s) => {
        try {
          const res = await fetch(`${DEEP_API_BASE}/sites/${s.site_id}/status`, { headers: apiHeaders() })
          if (res.ok) { const d = await res.json(); return d.connected ? s.site_id : null }
        } catch { /* ignore */ }
        return null
      }))
      if (!cancelled) setDeepConnectedIds(results.filter((id): id is string => !!id))
    }
    checkAll()
    return () => { cancelled = true }
  }, [sites, globalRefreshVersion])

  const eligibleSites = sites.filter((s) => deepConnectedIds?.includes(s.site_id))

  useEffect(() => {
    if (eligibleSites.length === 1) setSelectedSiteId(eligibleSites[0].site_id)
    else if (selectedSiteId && !eligibleSites.find((s) => s.site_id === selectedSiteId)) setSelectedSiteId('')
  }, [eligibleSites, selectedSiteId])

  if (deepConnectedIds === null) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-text-muted" /></div>
  }

  if (eligibleSites.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 space-y-4 max-w-xl mx-auto text-center">
        <Server className="h-16 w-16 text-[#21759b]/40" />
        <div className="space-y-2">
          <h2 className="text-xl font-bold text-text-primary">Add a deeper connection first</h2>
          <p className="text-text-muted text-sm">Backend Access (SSH) requires a site with a genuine Deep Connection
            already in place -- connect one on the Deep Connection tab, then come back here.</p>
        </div>
      </div>
    )
  }

  // Corrected 2026-09-01, per direct instruction: the warning and
  // explanation for Backend Access must stay visible alongside the site
  // selector, not disappear until a site is chosen -- this is especially
  // important for the warning itself, which needs to be seen before a
  // customer decides which site to grant SSH access to, not just after.
  // This is the same content BackendAccessTab's own top always showed;
  // it now lives here so the selector never hides it.
  return (
    <div className="ops-wp-backend-content max-w-3xl mx-auto space-y-4">
      <div className="ops-wp-safety-warning flex gap-3 p-4 rounded-xl border-2 border-danger/40 bg-danger/5">
        <AlertTriangle className="h-5 w-5 text-danger shrink-0 mt-0.5" />
        <p className="text-xs text-danger leading-relaxed">
          <span className="font-bold">Do not add this credential lightly, as AI can get things wrong.</span> Do
          not use this credential without OpsBridge's gate closed, so a request can be verified and changes can
          be recorded or confirmed before anything gets written by the AI.
        </p>
      </div>
      <div className="text-center space-y-1">
        <p className="text-xs text-text-muted">Full server-level access -- front-end and backend, via SSH. This
          grants genuinely more than the Deep Connection or Extension credentials above; it reaches the
          actual server your site runs on, not just WordPress's own API surface.</p>
      </div>
      {eligibleSites.length > 1 && (
        <div className="max-w-sm mx-auto">
          <label className="text-xs text-text-muted block mb-1">Which site?</label>
          <select value={selectedSiteId} onChange={(e) => setSelectedSiteId(e.target.value)}
            className="ops-wp-site-select w-full !ml-0">
            <option value="">Select a site…</option>
            {eligibleSites.map((s) => <option key={s.site_id} value={s.site_id}>{s.name}{s.primary ? ' (primary)' : ''}</option>)}
          </select>
        </div>
      )}
      {selectedSiteId ? <BackendAccessTab key={selectedSiteId} siteId={selectedSiteId} siteUrl={eligibleSites.find(s => s.site_id === selectedSiteId)?.url || ''} /> : eligibleSites.length > 1 ? (
        <p className="text-sm text-text-muted text-center py-4">Choose a site above to continue.</p>
      ) : null}
    </div>
  )
}

// ─── Main Export ──────────────────────────────────────────────────────
// ─── Deep Uninstall Analysis (AD-061) ─────────────────────────────────

function DeepUninstallTabContent({ siteId, siteName, onChangeSite }: { siteId: string; siteName: string; onChangeSite?: () => void }) {
  const [slug, setSlug] = useState('')
  const [loading, setLoading] = useState(false)
  const [manifest, setManifest] = useState<any>(null)
  const [error, setError] = useState<string | null>(null)
  const [plugins, setPlugins] = useState<Array<{ slug: string; name: string; active: boolean; version: string; file: string }>>([])
  const [pluginsLoading, setPluginsLoading] = useState(true)
  const [uninstalling, setUninstalling] = useState(false)
  const [confirmUninstall, setConfirmUninstall] = useState(false)
  const [uninstallResult, setUninstallResult] = useState<{ success: boolean; message: string } | null>(null)

  useEffect(() => {
    setPluginsLoading(true)
    setPlugins([])
    setSlug('')
    setManifest(null)
    setError(null)
    fetch(`${API_BASE}/sites/${siteId}/plugins`, { headers: apiHeaders() })
      .then((r) => r.ok ? r.json() : null)
      .then((data) => {
        const list = data?.plugins || data || []
        setPlugins(
          list.map((p: any) => ({
            slug: (p.file || p.plugin || '').split('/')[0],
            name: p.name || p.file || '',
            active: p.active ?? true,
            version: p.version || '',
            file: p.file || p.plugin || ''
          })).filter((p: any) => p.slug && p.slug !== 'opsbridge-connector')
        )
      })
      .catch(() => {})
      .finally(() => setPluginsLoading(false))
  }, [siteId])

  const runAnalysis = async () => {
    if (!slug) return
    setLoading(true)
    setError(null)
    setManifest(null)
    try {
      const res = await fetch(`${DEEP_API_BASE}/deep-uninstall`, {
        method: 'POST',
        headers: { ...apiHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ slug })
      })
      const data = await res.json()
      if (data.manifest) setManifest(data.manifest)
      else if (data.error) setError(data.error)
      else setError('Unexpected response')
    } catch (err: any) {
      setError(err.message || 'Analysis failed')
    }
    setLoading(false)
  }

  const runUninstall = async () => {
    if (!slug) return
    // Find the full plugin file path (slug/slug.php or slug/main-file.php)
    const pluginEntry = plugins.find(p => p.slug === slug)
    if (!pluginEntry) return
    setUninstalling(true)
    setUninstallResult(null)
    try {
      const res = await fetch(`${DEEP_API_BASE}/uninstall-plugin`, {
        method: 'POST',
        headers: { ...apiHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ plugin: pluginEntry.file })
      })
      const data = await res.json()
      if (data.success || data.deleted) {
        setUninstallResult({ success: true, message: `${pluginEntry.name} has been uninstalled.` })
        setPlugins(prev => prev.filter(p => p.slug !== slug))
        setSlug('')
        setManifest(null)
        setConfirmUninstall(false)
      } else {
        setUninstallResult({ success: false, message: data.error || data.message || 'Uninstall failed' })
      }
    } catch (err: any) {
      setUninstallResult({ success: false, message: err.message || 'Uninstall failed' })
    }
    setUninstalling(false)
  }

  const stateConfig: Record<string, { bg: string; text: string; icon: string; label: string }> = {
    SAFE: { bg: 'bg-green-500/10', text: 'text-green-400', icon: '🟢', label: 'Safe' },
    DATA_PRESENT: { bg: 'bg-yellow-500/10', text: 'text-yellow-400', icon: '🟡', label: 'Data Present' },
    UNKNOWN: { bg: 'bg-orange-500/10', text: 'text-orange-400', icon: '🟠', label: 'Unknown' },
    BLOCKED: { bg: 'bg-red-500/10', text: 'text-red-400', icon: '🔴', label: 'Blocked' },
  }

  return (
    <div className="space-y-3">
      {/* Site indicator + change button */}
      <div className="flex items-center justify-between bg-surface-elevated/50 rounded-lg px-2.5 py-1.5 border border-border/30">
        <div className="flex items-center gap-1.5 min-w-0">
          <Globe className="h-3 w-3 text-accent shrink-0" />
          <span className="text-[11px] text-text-muted truncate">{siteName}</span>
        </div>
        {onChangeSite && (
          <button onClick={onChangeSite} className="text-[11px] text-accent hover:underline cursor-pointer shrink-0 ml-2">
            Change site
          </button>
        )}
      </div>

      {/* Plugin selector + analyse */}
      <div className="space-y-1.5">
        <label className="text-[11px] font-medium text-text-muted">Plugin</label>
        <div className="relative">
          <select
            value={slug}
            onChange={(e) => { setSlug(e.target.value); setManifest(null); setError(null) }}
            disabled={pluginsLoading}
            className="w-full appearance-none bg-surface-elevated border border-border rounded-lg pl-3 pr-8 py-2 text-xs text-text-primary cursor-pointer focus:ring-1 focus:ring-accent focus:border-accent transition-colors"
          >
            <option value="">{pluginsLoading ? 'Loading...' : `Choose from ${plugins.length} plugins`}</option>
            {plugins.filter(p => !p.active).length > 0 && plugins.filter(p => p.active).length > 0 && (
              <optgroup label="Active">
                {plugins.filter(p => p.active).map((p) => (
                  <option key={p.slug} value={p.slug}>{p.name} {p.version ? `v${p.version}` : ''}</option>
                ))}
              </optgroup>
            )}
            {plugins.filter(p => !p.active).length > 0 && plugins.filter(p => p.active).length > 0 && (
              <optgroup label="Inactive">
                {plugins.filter(p => !p.active).map((p) => (
                  <option key={p.slug} value={p.slug}>{p.name} {p.version ? `v${p.version}` : ''}</option>
                ))}
              </optgroup>
            )}
            {(plugins.filter(p => !p.active).length === 0 || plugins.filter(p => p.active).length === 0) && (
              plugins.map((p) => (
                <option key={p.slug} value={p.slug}>{p.name} {p.version ? `v${p.version}` : ''}{p.active ? '' : ' (inactive)'}</option>
              ))
            )}
          </select>
          <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-text-muted pointer-events-none" />
        </div>

        <button
          onClick={runAnalysis}
          disabled={!slug || loading}
          className={cn(
            'w-full py-2 rounded-lg text-xs font-semibold transition-all',
            'shadow-[0_2px_8px_rgba(0,0,0,0.12),inset_0_1px_0_rgba(255,255,255,0.15)]',
            slug && !loading
              ? 'bg-accent text-white hover:brightness-110 cursor-pointer active:scale-[0.99]'
              : 'bg-card-bg text-text-muted cursor-not-allowed opacity-60'
          )}
        >
          {loading ? (
            <span className="flex items-center justify-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              Scanning footprint...
            </span>
          ) : (
            <span className="flex items-center justify-center gap-2">
              <Database className="h-4 w-4" />
              Analyse Plugin Footprint
            </span>
          )}
        </button>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
          <p className="text-red-400 text-sm">{error}</p>
        </div>
      )}

      {/* Manifest results */}
      {manifest && !loading && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {(['SAFE', 'DATA_PRESENT', 'UNKNOWN', 'BLOCKED'] as const).map((state) => {
              const count = manifest.summary?.[state.toLowerCase()] ?? 0
              const c = stateConfig[state]
              return (
                <div key={state} className={cn('rounded-lg p-3 text-center', c.bg)}>
                  <div className="text-2xl font-bold">{c.icon} {count}</div>
                  <div className={cn('text-xs font-medium mt-1', c.text)}>{c.label}</div>
                </div>
              )
            })}
          </div>

          <p className="text-xs text-text-muted text-center">
            {manifest.summary?.total ?? 0} resources analysed for <strong className="text-text-primary">{manifest.plugin}</strong>
          </p>

          {(['BLOCKED', 'UNKNOWN', 'DATA_PRESENT', 'SAFE'] as const).map((state) => {
            const resources = (manifest.resources || []).filter((r: any) => r.state === state)
            if (resources.length === 0) return null
            const c = stateConfig[state]
            return (
              <div key={state} className="space-y-1.5">
                <h3 className={cn('text-xs font-semibold flex items-center gap-1.5 uppercase tracking-wide', c.text)}>
                  {c.icon} {c.label} ({resources.length})
                </h3>
                {resources.map((r: any, i: number) => (
                  <div key={i} className={cn('rounded-lg p-2.5 border border-border/30', c.bg, 'bg-opacity-40')}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-mono font-medium text-text-primary">{r.name}</span>
                      <span className="text-[9px] px-1 py-0.5 rounded bg-card-bg/80 text-text-muted uppercase tracking-wide">{(r.type || '').replace(/_/g, ' ')}</span>
                    </div>
                    <p className="text-[11px] text-text-muted mt-0.5">{r.reason}</p>
                    {r.rows != null && r.rows > 0 && <p className="text-[11px] text-text-muted">{r.rows.toLocaleString()} rows</p>}
                    {r.count != null && r.count > 0 && <p className="text-[11px] text-text-muted">{r.count.toLocaleString()} records</p>}
                  </div>
                ))}
              </div>
            )
          })}

          {manifest.preserved?.length > 0 && (
            <div className="rounded-lg border border-border/30 p-3 bg-card-bg/30">
              <h3 className="text-xs font-semibold text-text-primary mb-1.5 flex items-center gap-1.5">
                <Shield className="h-3.5 w-3.5 opacity-50" />
                What OPS preserves
              </h3>
              {manifest.preserved.map((p: any, i: number) => (
                <p key={i} className="text-[11px] text-text-muted leading-relaxed">
                  <strong className="text-text-primary">{p.category}</strong> — {p.reason}
                </p>
              ))}
            </div>
          )}

          <div className="rounded-lg bg-green-500/5 border border-green-500/20 p-2.5 text-center">
            <p className="text-[11px] text-green-400 font-medium">{manifest.note}</p>
          </div>

          <div className="rounded-lg bg-orange-500/5 border border-orange-500/20 p-2.5">
            <p className="text-[10px] text-orange-300/80 leading-relaxed text-center">
              This analysis is a guide only. It shows what OPS has detected about the plugin's footprint and relationships.
              Always verify findings with your own research before taking any action on your site.
            </p>
          </div>

          {/* Status messages */}
          {uninstallResult && (
            <div className={cn('rounded-lg p-2.5 border text-center',
              uninstallResult.success ? 'bg-green-500/10 border-green-500/30' : 'bg-red-500/10 border-red-500/30'
            )}>
              <p className={cn('text-xs font-medium', uninstallResult.success ? 'text-green-400' : 'text-red-400')}>
                {uninstallResult.message}
              </p>
            </div>
          )}

          {/* Step 1: Uninstall Plugin (removes files only) */}
          {!uninstallResult?.success && (
            <div className="rounded-lg border border-border/40 bg-card-bg/40 p-3 space-y-2">
              <div>
                <h4 className="text-xs font-semibold text-text-primary">Step 1 — Uninstall Plugin</h4>
                <p className="text-[10px] text-text-muted mt-0.5">
                  Removes the plugin's code files from your site. Database tables, settings, and any content
                  the plugin created will remain — nothing is lost. You can reinstall the plugin afterwards if needed.
                </p>
              </div>
              {!confirmUninstall ? (
                <button onClick={() => setConfirmUninstall(true)}
                  className="w-full py-2 rounded-lg text-xs font-semibold bg-red-500/10 text-red-400 border border-red-500/30 hover:bg-red-500/20 transition-all cursor-pointer">
                  <span className="flex items-center justify-center gap-2">
                    <Trash2 className="h-3.5 w-3.5" />
                    Uninstall Plugin Files
                  </span>
                </button>
              ) : (
                <div className="rounded-lg border border-red-500/40 bg-red-500/5 p-2.5 space-y-2">
                  <p className="text-[11px] text-red-300 text-center">
                    This will deactivate and delete <strong>{manifest.plugin}</strong>'s files. Data stays on your site.
                  </p>
                  <div className="flex gap-2">
                    <button onClick={() => setConfirmUninstall(false)}
                      className="flex-1 py-1.5 rounded-lg text-xs font-medium bg-card-bg text-text-muted border border-border cursor-pointer">Cancel</button>
                    <button onClick={runUninstall} disabled={uninstalling}
                      className="flex-1 py-1.5 rounded-lg text-xs font-semibold bg-red-500 text-white hover:bg-red-600 cursor-pointer disabled:opacity-50">
                      {uninstalling ? <span className="flex items-center justify-center gap-1.5"><Loader2 className="h-3 w-3 animate-spin" />Removing...</span> : 'Confirm'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

        </div>
      )}

      {/* Standalone Orphan Table Scanner */}
      <OrphanTableScanner />
    </div>
  )
}

function OrphanTableScanner() {
  const [scanning, setScanning] = useState(false)
  const [tables, setTables] = useState<any[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  const runScan = async () => {
    setScanning(true)
    setError(null)
    setTables(null)
    try {
      const res = await fetch(`${DEEP_API_BASE}/orphaned-tables`, { headers: apiHeaders() })
      const data = await res.json()
      if (data.orphaned_tables) setTables(data.orphaned_tables)
      else if (data.error) setError(data.error)
      else setTables([])
    } catch (err: any) { setError(err.message) }
    setScanning(false)
  }

  const dropTable = async (tableName: string) => {
    try {
      const res = await fetch(`${DEEP_API_BASE}/drop-orphan-table`, {
        method: 'POST',
        headers: { ...apiHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ table: tableName, confirm: 'yes' })
      })
      const data = await res.json()
      if (data.success || data.renamed_to || data.backup_table) {
        setTables(prev => prev?.map(t =>
          (t.table || t.name) === tableName
            ? { ...t, _state: 'renamed', _backup: data.renamed_to || data.backup_table }
            : t
        ) || null)
      } else {
        setError(data.error || 'Failed to rename table')
      }
    } catch (err: any) { setError(err.message) }
  }

  const restoreTable = async (backupName: string, originalName: string) => {
    try {
      const res = await fetch(`${DEEP_API_BASE}/restore-dropped-table`, {
        method: 'POST',
        headers: { ...apiHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ backup_table: backupName })
      })
      const data = await res.json()
      if (data.success || data.restored_to) {
        setTables(prev => prev?.map(t =>
          (t.table || t.name) === originalName
            ? { ...t, _state: undefined, _backup: undefined }
            : t
        ) || null)
      } else {
        setError(data.error || 'Failed to restore table')
      }
    } catch (err: any) { setError(err.message) }
  }

  const permanentDelete = async (backupName: string, originalName: string) => {
    try {
      const res = await fetch(`${DEEP_API_BASE}/permanently-delete-table`, {
        method: 'POST',
        headers: { ...apiHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ backup_table: backupName, confirm: 'yes' })
      })
      const data = await res.json()
      if (data.success || data.deleted) {
        setTables(prev => prev?.filter(t => (t.table || t.name) !== originalName) || null)
      } else {
        setError(data.error || 'Failed to permanently delete table')
      }
    } catch (err: any) { setError(err.message) }
  }

  return (
    <div className="rounded-lg border border-border/40 bg-card-bg/40 p-3 space-y-2 mt-3">
      <div>
        <h4 className="text-xs font-semibold text-text-primary">Scan for Orphan Tables</h4>
        <p className="text-[10px] text-text-muted mt-0.5">
          Find database tables left behind by plugins that are no longer installed.
          Tables are renamed first (recoverable) — permanent deletion is a separate step.
        </p>
      </div>

      <button onClick={runScan} disabled={scanning}
        className={cn(
          'w-full py-2 rounded-lg text-xs font-semibold transition-all',
          'shadow-[0_2px_8px_rgba(0,0,0,0.12),inset_0_1px_0_rgba(255,255,255,0.15)]',
          !scanning ? 'bg-accent text-white hover:brightness-110 cursor-pointer active:scale-[0.99]' : 'bg-card-bg text-text-muted cursor-not-allowed opacity-60'
        )}>
        {scanning ? (
          <span className="flex items-center justify-center gap-2"><Loader2 className="h-3.5 w-3.5 animate-spin" />Scanning...</span>
        ) : (
          <span className="flex items-center justify-center gap-2"><Database className="h-3.5 w-3.5" />Scan for Orphan Tables</span>
        )}
      </button>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-2">
          <p className="text-red-400 text-[11px]">{error}</p>
        </div>
      )}

      {tables !== null && tables.length === 0 && (
        <div className="rounded-lg bg-green-500/5 border border-green-500/20 p-2.5 text-center">
          <p className="text-xs text-green-400 font-medium">No orphan tables found. Your database is clean.</p>
        </div>
      )}

      {tables !== null && tables.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-[10px] text-text-muted">{tables.length} orphan table{tables.length !== 1 ? 's' : ''} found</p>
          {tables.map((t: any) => {
            const name = t.table || t.name
            const isRenamed = t._state === 'renamed'
            return (
              <div key={name} className={cn(
                'flex items-center justify-between rounded-lg px-2.5 py-1.5 border border-border/30',
                isRenamed ? 'bg-green-500/5' : 'bg-surface-elevated/50'
              )}>
                <div className="min-w-0 flex-1">
                  <p className="text-[11px] font-mono text-text-primary truncate">{name}</p>
                  {isRenamed ? (
                    <p className="text-[9px] text-green-400">Renamed to {t._backup}. Recoverable.</p>
                  ) : (
                    <p className="text-[9px] text-text-muted">{t.rows ? `${t.rows.toLocaleString()} rows` : 'empty'}{t.size ? ` · ${t.size}` : ''}</p>
                  )}
                </div>
                <div className="flex gap-1 shrink-0 ml-2">
                  {isRenamed ? (
                    <>
                      <button onClick={() => restoreTable(t._backup, name)}
                        className="px-2 py-1 rounded text-[10px] font-medium bg-green-500/10 text-green-400 border border-green-500/30 hover:bg-green-500/20 cursor-pointer transition-all">
                        Restore
                      </button>
                      <button onClick={() => permanentDelete(t._backup, name)}
                        className="px-2 py-1 rounded text-[10px] font-medium bg-red-500/10 text-red-400 border border-red-500/30 hover:bg-red-500/20 cursor-pointer transition-all">
                        Delete
                      </button>
                    </>
                  ) : (
                    <button onClick={() => dropTable(name)}
                      className="px-2.5 py-1 rounded text-[10px] font-medium bg-orange-500/10 text-orange-400 border border-orange-500/30 hover:bg-orange-500/20 cursor-pointer transition-all">
                      Rename
                    </button>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

function DeepUninstallPanel({ sites }: { sites: WpSiteMeta[] }) {
  const [deepConnectedIds, setDeepConnectedIds] = useState<string[] | null>(null)
  const [selectedSiteId, setSelectedSiteId] = useState('')
  const [showSiteSelector, setShowSiteSelector] = useState(false)
  const globalRefreshVersion = useAppStore((s) => s.globalRefreshVersion)

  useEffect(() => {
    let cancelled = false
    async function checkAll() {
      const results = await Promise.all(sites.map(async (s) => {
        try {
          const res = await fetch(`${DEEP_API_BASE}/sites/${s.site_id}/status`, { headers: apiHeaders() })
          if (res.ok) { const d = await res.json(); return d.connected ? s.site_id : null }
        } catch { /* ignore */ }
        return null
      }))
      if (!cancelled) setDeepConnectedIds(results.filter((id): id is string => !!id))
    }
    checkAll()
    return () => { cancelled = true }
  }, [sites, globalRefreshVersion])

  const eligibleSites = sites.filter((s) => deepConnectedIds?.includes(s.site_id))

  useEffect(() => {
    if (eligibleSites.length === 1 && !selectedSiteId) setSelectedSiteId(eligibleSites[0].site_id)
    else if (selectedSiteId && !eligibleSites.find((s) => s.site_id === selectedSiteId)) setSelectedSiteId('')
  }, [eligibleSites, selectedSiteId])

  if (deepConnectedIds === null) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-text-muted" /></div>
  }

  if (eligibleSites.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 space-y-4 max-w-xl mx-auto text-center">
        <Shield className="h-16 w-16 text-[#21759b]/40" />
        <h2 className="text-xl font-bold text-text-primary">Deep Connection required</h2>
        <p className="text-text-muted text-sm">Deep Uninstall Analysis needs a site with a Deep Connection. Add one on the Deep Connection tab first.</p>
      </div>
    )
  }

  // Site selector view
  if (showSiteSelector || !selectedSiteId) {
    return (
      <div className="ops-wp-card p-4 space-y-3">
        <p className="text-xs text-text-muted text-center">Select which site to analyse</p>
        <div className="space-y-1.5">
          {eligibleSites.map((s) => (
            <button key={s.site_id} onClick={() => { setSelectedSiteId(s.site_id); setShowSiteSelector(false) }}
              className={cn(
                'w-full text-left px-3 py-2.5 rounded-lg border transition-all cursor-pointer',
                'hover:border-accent/50 hover:bg-accent/5',
                selectedSiteId === s.site_id ? 'border-accent bg-accent/5' : 'border-border bg-surface-elevated'
              )}>
              <div className="flex items-center gap-2">
                <Globe className="h-3.5 w-3.5 text-accent shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-text-primary truncate">{s.name}</p>
                  <p className="text-[10px] text-text-muted truncate">{s.url}</p>
                </div>
                <ArrowRight className="h-3 w-3 text-text-muted ml-auto shrink-0" />
              </div>
            </button>
          ))}
        </div>
      </div>
    )
  }

  const activeSite = eligibleSites.find((s) => s.site_id === selectedSiteId)

  return (
    <div className="ops-wp-card p-4">
      <DeepUninstallTabContent
        siteId={selectedSiteId}
        siteName={activeSite?.name || activeSite?.url || selectedSiteId}
        onChangeSite={eligibleSites.length > 1 ? () => setShowSiteSelector(true) : undefined}
      />
    </div>
  )
}
export function WordPressPanel() {
  const [sites, setSites] = useState<WpSiteMeta[]>([])
  const [loading, setLoading] = useState(true)
  const [view, setView] = useState<'landing' | 'form' | 'dashboard'>('landing')
  const [activeTab, setActiveTab] = useState<'health' | 'deep' | 'backend' | 'uninstall' | 'capabilities'>('health')
  const globalRefreshVersion = useAppStore((s) => s.globalRefreshVersion)

  const fetchSites = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/sites`, { headers: apiHeaders() })
      if (res.ok) {
        const data = await res.json()
        const s = data.sites || []
        setSites(s)
        setView(s.length > 0 ? 'dashboard' : 'landing')
      }
    } catch { /* ignore */ }
    setLoading(false)
  }, [])

  useEffect(() => { fetchSites() }, [fetchSites, globalRefreshVersion])

  const tabDescriptions: Record<string, string> = {
    health: 'View your site\'s WordPress version, PHP version, plugin status, database health, and error logs. This is where you connect and monitor your WordPress sites.',
    deep: 'Connect your site using its URL and the Deep Token from the OPS Connector plugin settings page. This gives OPS secure post and page creation and editing, deeper diagnostics, and access to supported WCFM marketplace data, while remaining separate from the standard Health connection.',
    backend: 'Server-level access via SSH for advanced operations. This grants direct filesystem access to your hosting environment — use with caution.',
    uninstall: 'Scan any installed plugin to see exactly what it has added to your site — database tables, settings, scheduled tasks, and content types. OPS analyses the plugin\'s footprint, identifies what it owns, and shows you what would remain if removed. Use this before uninstalling to understand dependencies and avoid leaving orphaned data behind.',
    capabilities: 'See what OPS can do with your connected WordPress site — from content management to marketplace operations.',
  }

  const tabs = (
    <div className="space-y-3 mb-4">
      {activeTab === 'health' || activeTab === 'deep' || activeTab === 'backend' || activeTab === 'uninstall' || activeTab === 'capabilities' ? (
        <div className="ops-wp-intro">
          <span className="ops-wp-intro-icon">
            {activeTab === 'health' ? <Activity className="h-5 w-5" />
              : activeTab === 'deep' ? <Shield className="h-5 w-5" />
                : activeTab === 'backend' ? <Server className="h-5 w-5" />
                  : activeTab === 'uninstall' ? <Trash2 className="h-5 w-5" />
                    : <Cable className="h-5 w-5" />}
          </span>
          <div>
            <h2 className="text-lg font-bold text-text-primary">
              {activeTab === 'health' ? 'WordPress Health'
                : activeTab === 'deep' ? 'Deep Connection'
                  : activeTab === 'backend' ? 'Backend Access'
                    : activeTab === 'uninstall' ? 'Safe Plugin Uninstall'
                      : 'WordPress Integration'}
            </h2>
            <p className="text-xs text-text-muted mt-0.5 leading-relaxed">{tabDescriptions[activeTab]}</p>
          </div>
        </div>
      ) : (
        <div className="relative rounded-2xl border p-3 overflow-hidden bg-white/75 border-white/60 shadow-[0_4px_24px_rgba(0,0,0,0.07),0_1px_4px_rgba(0,0,0,0.04)] backdrop-blur-md dark:bg-black/[0.28] dark:border-white/[0.10] dark:shadow-[0_4px_32px_rgba(0,0,0,0.5),inset_0_1px_0_rgba(255,255,255,0.06)] dark:backdrop-blur-md">
          <p className="text-xs text-text-primary/80 leading-relaxed">{tabDescriptions[activeTab]}</p>
        </div>
      )}
      <div className="ops-wp-tabs scrollbar-none" style={{ WebkitOverflowScrolling: 'touch' }}>
        {([
          { id: 'health' as const, label: 'Health' },
          { id: 'deep' as const, label: 'Connection' },
          { id: 'backend' as const, label: 'Backend' },
          { id: 'uninstall' as const, label: 'Uninstall' },
          { id: 'capabilities' as const, label: 'Integration' },
        ]).map((tab) => {
          const isActive = activeTab === tab.id
          return (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={cn(
                'ops-wp-tab',
                isActive ? 'is-active' : ''
              )}>
              {tab.label}
            </button>
          )
        })}
      </div>
    </div>
  )

  // Deep Connection and Backend Access restored as real top-level tabs
  // 2026-09-01, correcting an earlier design that nested them at the
  // bottom of each site's Health row instead -- the actual requirement
  // was the gating logic (no Deep Connection without a Health-connected
  // site, no SSH without a genuine Deep Connection for that same site),
  // enforced here by DeepConnectionTabWithSelector/BackendAccessTabWith
  // Selector's own real site-eligibility checks, not by burying the
  // controls in a different tab.
  if (activeTab === 'deep') return <div className="ops-wp-health-page ops-wp-deep-page">{tabs}<DeepConnectionTabWithSelector sites={sites} /></div>
  if (activeTab === 'backend') return <div className="ops-wp-health-page ops-wp-deep-page ops-wp-backend-page">{tabs}<BackendAccessTabWithSelector sites={sites} /></div>
  if (activeTab === 'uninstall') return <div className="ops-wp-health-page ops-wp-deep-page ops-wp-uninstall-page">{tabs}<DeepUninstallPanel sites={sites} /></div>
  if (activeTab === 'capabilities') return <div><div className="ops-wp-health-page ops-wp-integration-top">{tabs}</div><WordPressCapabilitiesShowcase showConnectCta={sites.length === 0} onConnect={() => setView('form')} /></div>

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-text-muted" /></div>

  if (view === 'form') return <div>{tabs}<ConnectionForm onSuccess={fetchSites} onCancel={() => setView(sites.length > 0 ? 'dashboard' : 'landing')} /></div>
  if (view === 'dashboard' && sites.length > 0) return <div className="ops-wp-health-page">{tabs}<ConnectedDashboard sites={sites} onAddSite={() => setView('form')} onRefresh={fetchSites} /></div>
  return <div>{tabs}<DisconnectedLanding onConnect={() => setView('form')} /></div>
}
